/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2017 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
/* global navigator, window */
/* eslint-disable no-underscore-dangle */

import uuid from "uuid";
import merge from "lodash.merge";
import AdobeDC from "adobe-dcapi-web";
import { enums as AJSEnums } from "@ajsprivate/acrobatjs";
import { version as AJSVersion } from "@ajsprivate/acrobatjs/package.json";
import { providers as serviceAPI, auth2 as auth, webPlatform } from "dc-core";
import { getSingletonFunction } from "dc-core/dist/cjs/core/ProviderUtil";

import { setDocumentId, logEvent } from "./logging";
// TODO: below file is as of now duplicated in preview dropin and here/ we should try
// to put this file into @ajsprivate/acrobatjs.
import { cancelAllPromises, renditionMakeCancellablePromise } from "./utils/renditionCancellablePromise";
import { getSHA256Digest } from "./utils/DataEncoding";
import * as ajsPathUtil from "./utils/ajsPathUtils";
import * as constants from "./utils/constants";
import { getQueryParams } from "./utils/queryParseUtils";
import SecurityHandler from "./utils/securityHandler";
import fetchCNFTPDFUpdatesCallback from "./utils/AdobeSenseiPredictClient";
import { cloneDeep, delay, getVariablesFromQueryParams } from "./utils/utils";
import { getCertificateValidators } from "./digitalSignature";
import { DCAPI_REDIRECT_URL_MAX_RETRIES } from "./utils/constants";
import { recordNow } from "./utils/performance";
import AJSHandler from "./AJSHandler";
import updateETagForViewerAssetSelection from "./utils/ProviderUtil";

// Keep a reference to the singleton instance
// using this bypasses the issue where references such as: this.property
// end up going through the getter set up by serviceAPI
let self = null;

class NoRenditionError extends Error {
    constructor(message) {
        super(message);
        this.code = 415;
    }
}

// yguptaTODO - stop exporting the class after fixing the unit tests
// to use discovery way of loading the class
export class RenditionAPI {
    constructor(auth, config) { // eslint-disable-line no-shadow
        self = this;

        this.queryParams = getQueryParams();

        this.options = Object.assign(
            {},
            {
                enableExternal: true,
                useAJS: true,
                useDCAPI: true,
                AJSDelay: 0,
                useReview: true,
                usePDFNow: false,
                fetchWithCredentials: false,
                autoStart: true,
                useALMForAutoStart: false,
                shouldUseModernViewer: false,
                enableAJSLogging: false,
                useAJSFullBuild: false,
                useProgressiveRendering: true,
            },
            config,
            getVariablesFromQueryParams(this.queryParams),
        );

        this.ajsHandler = new AJSHandler(this.options, this._onAjsEvent.bind(this));
        this._fetchCompletePDFBufferPromise = null;
        this._assetRenditions = {};
        this._assetRenditionsIndex = [];
        this._assetPageRenditions = {};
        this._assetPageRenditionPromises = {};
        this._assetPageRenditionsIndex = [];
        this._cachedRedirectUrl = {};
        this._perfTimings = {};
        this._resetAjsPerfTimings();
        this._getAssetFileInfoPromise = {};

        if (this.options.autoStart) this.init();
        return self;
    }

    // eslint-disable-next-line class-methods-use-this
    _getAjs() {
        return this.ajsHandler.init().catch(error => {
            this.options.useAJS = false;
            this._ajsEmit("ajsInitFailed", error);
        });
    }

    // reinitialize workers
    reInitAJS() {
        this.ajsHandler.resetWorker();
        this._resetAjsPerfTimings();
    }

    _appStateChangeListener = newState => {
        switch (newState) {
        case "DEFERRED_LOAD":
            this._getAjs();
            break;
        default:
            break;
        }
    };

    /**
     * init function - initialize workers
     */
    init() {
        if (this.options.useDCAPI) {
            this.dcapiPromise = new Promise((resolve, reject) => {
                this.dcapiResolve = resolve;
                this.dcapiReject = reject;
            });
            this._dcapiInit();
        } else {
            this.dcapiPromise = Promise.resolve();
        }
        if (this.options.useAJS) {
            this._completePDFBufferAvailablePromiseMap = {};
            this._completeCNPDFAvailablePromiseMap = {};
            // after some idle time, download the ajs wasm for future use
            // Since commerce loads after 10 seconds, we'll load this after 12 seconds
            if (this.options.AJSDelay > 0) {
                setTimeout(this._getAjs.bind(this), this.options.AJSDelay);
            } else if (this.options.useALMForAutoStart) {
                document.addEventListener("dc:appstatechange", event => {
                    const newState = event.detail?.appState;
                    this._appStateChangeListener(newState);
                });
            } else {
                this._getAjs();
            }
        }

        this._waitOnCompletePDFBufferMap = {};
        this._ajsMetaDataPromiseMap = {};
    }

    _resetAjsPerfTimings() {
        if (this.options.useAJS) {
            this._perfTimings.ajs = {};
        }
    }

    /**
     * Gets fileInfo for a dc web asset. fileInfo contains metadata and content related information.
     * Returns a promise which is resolved when both of these are available.
     */
    _getDCAssetFileInfo(assetUri, options = {}) {
        const assetKey = `${assetUri}`;
        if (this._getAssetFileInfoPromise[assetKey]) {
            return this._getAssetFileInfoPromise[assetKey];
        }
        this._getAssetFileInfoPromise[assetKey] = this.dcapiPromise
            .then(() => {
                const metaDataPromise = this._dcapiGetMetadata(assetUri, options);
                const downloadUrlPromise = this._dcapiGetDownloadUri(assetUri, {});
                return Promise.all([downloadUrlPromise, metaDataPromise]);
            })
            .then(result => {
                const downloadUri = result[0];
                const metadata = result[1];
                return this._getFileInfoObj(downloadUri, metadata, []);
            })
            .catch(() => {
                delete this._getAssetFileInfoPromise[assetKey];
            });
        return this._getAssetFileInfoPromise[assetKey];
    }

    _getPDFNowAssetFileInfo(assetUri, options) {
        return this.dcapiPromise
            .then(() => this._dcapiGetMetadata(assetUri, options))
            .then(result => {
                const metaData = result;
                if (metaData && !("id" in metaData)) {
                    metaData.id = metaData.asset_id;
                }
                return { metaData };
            });
    }

    /**
     * Gets fileInfo for a review asset. fileInfo contains metadata and content related information.
     * Returns a promise which is resolved when both of these are available.
     */
    _getReviewAssetFileInfo(assetUri, isACP = false) {
        const assetKey = `${assetUri}`;
        const assetURLPromise = window.adobe_dc_sdk.cache.assetURLPromise &&
            window.adobe_dc_sdk.cache.assetURLPromise[assetUri];
        if (this._getAssetFileInfoPromise[assetKey]) {
            return this._getAssetFileInfoPromise[assetKey];
        }
        // For ACP assets and signed-in users, Always use DC API metadata
        let metaDataPromise;
        if (isACP && auth.isSignedIn) {
            metaDataPromise = this._dcapiGetMetadata(assetUri, {});
        } else {
            metaDataPromise = this._reviewGetMetadata(assetUri);
        }
        const downloadUrlPromise = assetURLPromise || this._reviewGetDownloadUri(assetUri, isACP);

        this._getAssetFileInfoPromise[assetKey] = Promise.allSettled([downloadUrlPromise, metaDataPromise])
            .then(async result => {
                let downloadUri;
                const isResolvedPromise = (result[0].status === "fulfilled" && result[0].value && result[0].value !== "RESOLVED_WITH_NOT_FOUND");
                if (isResolvedPromise) {
                    downloadUri = assetURLPromise ? result[0].value.download_url : result[0].value;
                } else {
                    downloadUri = await this._reviewGetDownloadUri(assetUri, isACP);
                }
                if (!downloadUri) {
                    throw new Error("Download Uri is null");
                }
                const metadata = result[1].status === "fulfilled" ? result[1].value : null;
                return this._getFileInfoObj(downloadUri, metadata, []);
            })
            .catch(() => {
                delete this._getAssetFileInfoPromise[assetKey];
            });
        return this._getAssetFileInfoPromise[assetKey];
    }

    _getFileInfoObj = (url, metaData, headers = []) => {
        // not include auth header for s3 download urls
        if (auth.isSignedIn && !this._isS3DownloadUrl) {
            const info = auth.getAccessTokenInfo();
            if (info.access_token) {
                headers.push({
                    key: "Authorization",
                    value: `Bearer ${info.access_token}`,
                });
            }
        }
        // View SDK bundle needs id property in metadata rather than asset_id
        if (metaData && !("id" in metaData)) {
            metaData.id = metaData.asset_id; // eslint-disable-line no-param-reassign
        }
        return {
            content: {
                location: {
                    url,
                    headers,
                },
            },
            metaData,
        };
    };

    /**
     * Gets fileInfo for a given assetUri and assetType.
     */
    getAssetFileInfo(assetUri, options) {
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            return this._getReviewAssetFileInfo(assetUri);
        }
        if (RenditionAPI._isPDFNowAsset(options)) {
            return this._getPDFNowAssetFileInfo(assetUri, options);
        }
        if (RenditionAPI._isConnectorAsset(options)) {
            return this._connectorGetMetadata(assetUri, options).then(metaData => {
                if (metaData && !("id" in metaData)) {
                    metaData.id = metaData.asset_id; // eslint-disable-line no-param-reassign
                }
                if (metaData.download_url) {
                    return Promise.resolve({
                        content: {
                            location: {
                                url: metaData.download_url,
                            },
                        },
                        metaData,
                    });
                }
                return this._connectorGetContentBuffer(assetUri, options).then(content => {
                    const blob = new Blob([content]);
                    return Promise.resolve({
                        content: {
                            location: {
                                url: URL.createObjectURL(blob),
                            },
                        },
                        metaData,
                    });
                });
            });
        }
        return this._getDCAssetFileInfo(assetUri);
    }

    /**
     * Public wrapper method over _dcapiGetRendition
     */
    getDcapiRendition(assetUri, options) {
        return this._dcapiGetRendition(assetUri, options);
    }

    static _getOriginFromUrl(url) {
        return ajsPathUtil.getOriginFromUrl(url);
    }

    /**
     * This function handles the payload corresponding to the ajs event name received in args.
     * - If the event name is *{initiator}PerfTimings*, the payload is appended to perfTimings object
     * - Else documentId is appended to the payload and sent for logging
     * @param {string} eventName eventName
     * @param {Object}  payload
     * @param {string}  initiator the module which initiates this event
     *
     * @private
     */

    _ajsEmit(eventName, payload) {
        const finalPayload = {
            ...payload,
            acrobatJSVersion: AJSVersion,
            shouldUseModernViewer: this.options.shouldUseModernViewer,
        };
        logEvent(eventName, finalPayload);
    }

    _onAjsEvent(assetUri, eventName, payload, initiator) {
        const perftimingEventName = `${initiator}PerfTimings`;
        switch (eventName) {
        case "completeCNPDFAvailable":
            // TODO: Move the Linearization code from rendition Provider to AJS, ADCS-55358
            this._completePDFBufferAvailablePromiseMap[assetUri].resolve();
            this._completeCNPDFAvailablePromiseMap[assetUri].resolve();
            break;
        case "completePDFBufferAvailable":
            this._completePDFBufferAvailablePromiseMap[assetUri].resolve();
            break;
        case perftimingEventName:
            if (initiator === "ajs") {
                Object.keys(payload).forEach(key => recordNow(key, payload[key]));
            }
            this._perfTimings[initiator] = Object.assign({}, this._perfTimings[initiator], payload);
            break;
        case "FontUsed":
            serviceAPI["font-provider"]().then(fontProvider => {
                if (!payload.isInEditMode && payload.fontsList) {
                    payload.fontsList.forEach(fontName => {
                        if (fontProvider.isUsageUnReported(fontName)) {
                            this._ajsEmit("FontUsageNotReported", { fontName });
                        }
                    });
                }
            });
            break;
        default:
            this._ajsEmit(eventName, payload);
        }
    }

    /**
     * Initializes DCAPI for use by the provider.
     * Adds _dcapi object in self.
     *
     * @private
     */
    _dcapiInit() {
        this._perfTimings.dc = { dcapiInit: recordNow(constants.perfMarkers.DCAPI_INIT) };
        if (serviceAPI && serviceAPI.dcapi) {
            serviceAPI.dcapi().then(dcapi => {
                this._dcapi = dcapi.getDcapi();
                this._dcapi.promise().then(() => {
                    this._perfTimings.dc.dcapiLoaded = recordNow(constants.perfMarkers.DCAPI_LOADED);
                    this.dcapiResolve();
                }).catch(err => {
                    logEvent(err, { code: err.code, level: "error" });
                    this.dcapiReject();
                });
            });
        }
    }

    /**
     * Initializes pdfnow api promise.
     *
     * @private
     */
    _pdfnowInitPromise() {
        if (this.pdfnowApiPromise) {
            return false;
        }
        if (!this.options.usePDFNow) {
            this.pdfnowApiPromise = Promise.resolve();
            return false;
        }
        this.pdfnowApiPromise = new Promise((resolve, reject) => {
            this.pdfnowApiResolve = resolve;
            this.pdfnowApiReject = reject;
        });
        return true;
    }

    /**
     * Loads PDFNow library for use by the provider
     *
     * @private
     */
    _pdfnowInit() {
        if (serviceAPI !== undefined) {
            serviceAPI["dcapi-facade"]().then(dcapiFacade => dcapiFacade.getDcapi().then(d => {
                this._pdfnowDcapi = d;
                this.pdfnowApiResolve();
            })).catch(err => {
                logEvent(err, { code: err.code, level: "error" });
                this.pdfnowApiReject();
            });
        }
    }

    _resetReviewProvider() {
        this.reviewApiPromise = undefined;
        this._initReviewProviderIfRequired();
    }

    _initReviewProviderIfRequired() {
        if (this._reviewInitPromise()) {
            this._reviewInit();
        }
    }

    /**
     * Initializes review api promise.
     *
     * @private
     */
    _reviewInitPromise() {
        if (this.reviewApiPromise) {
            return false;
        }
        if (!this.options.useReview) {
            this.reviewApiPromise = Promise.resolve();
            return false;
        }
        this.reviewApiPromise = new Promise((resolve, reject) => {
            this.reviewApiResolve = resolve;
            this.reviewApiReject = reject;
        });
        return true;
    }

    /**
     * Loads Review library for use by the provider
     * Adds _review object in self
     *
     * @private
     */
    _reviewInit() {
        if (serviceAPI !== undefined) {
            serviceAPI["review-handler"]().then(reviewApi => {
                this._reviewApi = reviewApi;
                this.reviewApiResolve();
            }).catch(err => {
                logEvent(err, { code: err.code, level: "error" });
                this.reviewApiReject();
            });
        }
    }

    /**
     * Initializes external api promise.
     *
     * @private
     */
    _externalInitPromise() {
        if (this.externalPromise) {
            return false;
        }
        if (!this.options.enableExternal) {
            this.externalPromise = Promise.resolve();
            return false;
        }
        this.externalPromise = new Promise((resolve, reject) => {
            this.externalResolve = resolve;
            this.externalReject = reject;
        });
        return true;
    }

    /**
     * Loads Integration library for use by the provider
     *
     * @private
     */
    _externalInit() {
        if (serviceAPI !== undefined) {
            serviceAPI.integrationProvider().then(integrationProvider => {
                this._external = integrationProvider;
                this.externalResolve();
            }).catch(err => {
                logEvent(err, { code: err.code, level: "error" });
                this.externalReject();
            });
        }
    }

    _cacheRendition(assetUri, dataObject) {
        if (!this._assetRenditions[assetUri]) {
            if (this._assetRenditionsIndex.length === constants.CACHE_SIZE) {
                delete this._assetRenditions[this._assetRenditionsIndex.shift()];
            }
            this._assetRenditionsIndex.push(assetUri);
        }
        this._assetRenditions[assetUri] = merge(this._assetRenditions[assetUri], dataObject);
    }

    _cachePageRendition(assetUri, page, dataObject) {
        const k = `${assetUri}-${page}`;
        if (!this._assetPageRenditions[k]) {
            if (this._assetPageRenditionsIndex.length === constants.CACHE_SIZE) {
                delete this._assetPageRenditions[this._assetPageRenditionsIndex.shift()];
            }
            this._assetPageRenditionsIndex.push(k);
        }
        this._assetPageRenditions[k] = merge(this._assetPageRenditions[k], dataObject);
    }

    /**
     * Gets DCAPI asset URI given a assetURN or assetURI.
     *
     * @private
     */
    _dcapiGetAssetUri(assetUri) {
        if (this._dcapi && assetUri.startsWith("urn:")) {
            return this._dcapi.getTemplateURI("asset_uri", { asset_id: assetUri });
        }
        return assetUri;
    }

    /**
     * Gets DCAPI asset URN given a assetURN or assetURI.
     *
     * @private
     */
    static _dcapiGetAssetUrn(assetUri) {
        // yguptaTODO: This method should not be needed when everyone calls the rendition
        // method with asset URN. Remove the use of this method once that gets fixed
        // across the board.
        if (assetUri.startsWith("urn:")) {
            return assetUri;
        }
        return assetUri.substr(assetUri.lastIndexOf("/") + 1);
    }

    static _isFileTypeImage(fileType) {
        const regexForImageType = new RegExp("^image");
        return regexForImageType.test(fileType);
    }

    static _canFileHaveMultiplePages(fileType) {
        if (RenditionAPI._isFileTypeImage(fileType)) return false;
        const singlePageFileTypes = ["application/zip", "application/octet-stream", "text/plain", "text/rtf"];
        return !singlePageFileTypes.includes(fileType);
    }

    _dcapiMetadataCall(assetUri, options, tryCount = 1) {
        return new Promise((resolve, reject) => {
            const p1 = this._dcapi.call(
                "assets.get_metadata",
                {
                    accept: "asset_metadata_basic_v1.json",
                    resource_parameter: this._dcapiGetAssetUri(assetUri),
                    headers: { "x-request-id": uuid.v4() },
                },
            );

            p1.then(
                result => {
                    const meta = result;
                    // eslint-disable-next-line camelcase
                    const numPages = meta?.content?.page_count;
                    let p;
                    const metaContentType = meta?.content?.type;
                    meta.content.numPages = numPages;
                    if (options.enableRetryForPDFs ||
                        this.queryParams.enableRetryForPDFs ||
                        metaContentType !== "application/pdf") {
                        // if page_count not present
                        if (!numPages) {
                            // retry
                            let maxRetries = metaContentType === "application/pdf" ?
                                constants.DCAPI_PDF_METADATA_MAX_RETRIES :
                                constants.DCAPI_NON_PDF_METADATA_MAX_RETRIES;
                            if (!RenditionAPI._canFileHaveMultiplePages(metaContentType)) {
                                maxRetries = 1;
                            }
                            const interval = meta.content.type === "application/pdf" ?
                                constants.RETRY_INTERVAL :
                                constants.RETRY_INTERVAL_NON_PDF;
                            if (tryCount === maxRetries) {
                                meta.content.numPages = 1;
                            } else {
                                p = new Promise((metadataResolve, metadataReject) => {
                                    setTimeout(() => {
                                        this._dcapiMetadataCall(assetUri, options, tryCount + 1)
                                            .then(metadataResolve)
                                            .catch(metadataReject);
                                    }, interval);
                                });
                            }
                        }
                    }
                    if (!p) {
                        p = Promise.resolve(meta);
                    }
                    p.then(resolve).catch(reject);
                },
                err => {
                    reject(err);
                },
            );
        });
    }

    /**
     * Gets metadata of file via DCAPI.
     * Returns Promise which resolves when metadata is available
     *
     * @private
     */
    _dcapiGetMetadata(assetUri, options) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.contentData ||
            options.forceCacheless === true) {
            if (RenditionAPI._isReviewAsset(options) ||
                RenditionAPI._isParcelAsset(options)) {
                return this._reviewGetMetadata(assetUri, options);
            }

            if (RenditionAPI._isPDFNowAsset(options)) {
                return this._pdfnowGetMetadata(assetUri, options);
            }

            if (RenditionAPI._isAcpAsset(options)) {
                return this._acpGetMetadata(assetUri, options);
            }

            if (RenditionAPI._isConnectorAsset(options)) {
                return this._connectorGetMetadata(assetUri, options);
            }

            return new Promise((resolve, reject) => {
                this.dcapiPromise.then(() => {
                    const metadataPromise = this._dcapiMetadataCall(assetUri, options);
                    const renditionStatusPromise = this._dcapiGetDocumentRenditionStatus(assetUri);

                    Promise.all([metadataPromise, renditionStatusPromise])
                        .then(([metadataResp, renditionStatusResp]) => {
                            this._cacheRendition(assetUri, {
                                dcapi: {
                                    contentData: cloneDeep(metadataResp.content),
                                },
                            });
                            this._cacheRendition(assetUri, {
                                dcapi: {
                                    contentData: {
                                        documentRenditionStatus: renditionStatusResp,
                                    },
                                },
                            });
                            resolve(this._assetRenditions[assetUri].dcapi.contentData);
                        }).catch(reject);
                });
            });
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.contentData);
    }

    /**
     * Utility to check if the asset is a review asset or not.
     *
     * @private
     */
    static _isReviewAsset(options) {
        return (options.assetType === constants.REVIEW_TYPE);
    }

    /**
     * Utility to check if the asset is a parcel asset or not.
     *
     * @private
     */
    static _isParcelAsset(options) {
        return (options.assetType === constants.PARCEL_TYPE);
    }

    /**
     * Utility to check if the asset is a external asset or not.
     *
     * @private
     */
    static _isExternalAsset(options) {
        return (options.assetType === constants.EXTERNAL_TYPE);
    }

    /**
     * Utility to check if the asset is a supported connector asset or not.
     *
     * @private
     */
    static _isConnectorAsset(options) {
        return (options.assetType && constants.CONNECTOR_ASSET_TYPES.includes(options.assetType));
    }

    /**
     * Utility to check if the asset is a sign asset or not.
     *
     * @private
     */
    static _isSignAsset(options) {
        return (options.assetType === constants.SIGN_TYPE);
    }

    static _isAcpAsset(options) {
        return (options.assetType === constants.ACP_TYPE);
    }

    /**
     * Utility to check if the asset is a pdfnow asset or not.
     *
     * @private
     */
    static _isPDFNowAsset(options) {
        return (options.assetType === constants.PDFNOW_TYPE);
    }

    /**
     * Utility to check if the asset is a PDF file or not via DCAPI.
     * If DCAPI is disabled, returns a resolved promise.
     *
     * @private
     */
    _dcapiIsPDFAsset(assetUri, options) {
        return new Promise((resolve, reject) => {
            if (options.useDCAPI) {
                this._dcapiGetMetadata(assetUri, options)
                    .then(() => {
                        if (!this._assetRenditions[assetUri] ||
                            this._assetRenditions[assetUri].dcapi.contentData.type !== "application/pdf") {
                            reject();
                        } else {
                            resolve();
                        }
                    }).catch(reject);
            } else {
                resolve();
            }
        });
    }

    /**
     * Utility to check if the asset is a PDF file or not via ACP API.
     * Do not rely on DCAPI api check as it is ACP asset and directly
     * interacting with ACP through SAMJS for metadata and renditions.
     *
     * @private
     */
    _acpapiIsPDFAsset(assetUri, options) {
        return new Promise((resolve, reject) => {
            this._acpGetMetadata(assetUri, options)
                .then(() => {
                    if (!this._assetRenditions[assetUri] ||
                    this._assetRenditions[assetUri].acp.metaData.mimeType !== "application/pdf") {
                        reject();
                    } else {
                        resolve();
                    }
                }).catch(reject);
        });
    }

    /**
     * Utility to check if the asset is a PDF file or not.
     * Determines whether the asset is a PDF by looking at the "type"
     * argument in options. If "type" is not present, fetches the metadata
     * via DCAPI (if enabled).
     *
     * @private
     */
    _isPDFAsset(assetUri, options) {
        return new Promise((resolve, reject) => {
            if (options && options.assetMimeType === "application/pdf") {
                resolve();
            } else if (RenditionAPI._isAcpAsset(options)) {
                this._acpapiIsPDFAsset(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else {
                this._dcapiIsPDFAsset(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            }
        });
    }

    _ajsPostMetaData(assetUri, options) {
        const buffer = options.buffer;
        if (buffer) {
            this._fetchCompletePDFBufferPromise = Promise.resolve(buffer);
            delete options.buffer;
        } else if (!options.enableCNPDF) {
            this._fetchCompletePDFBufferPromise = this._fetchCompletePDFBuffer(assetUri, options);
        }

        if (options.enableLinearization && !options.password) {
            return renditionMakeCancellablePromise(this._ajsGetLinearizedContentData(assetUri, options)); //eslint-disable-line
        }
        if (options.enableCNPDF) {
            return renditionMakeCancellablePromise(this._ajsGetCNPDFContentData(assetUri, options));
        }

        return this._fetchCompletePDFBufferPromise
            .then(buf => renditionMakeCancellablePromise(this._ajsGetContentData(assetUri, buf, options))
                .catch(error => {
                    logEvent("ajsGetContentData Failed", {
                        error: JSON.stringify(error),
                        size: buf ? buf.byteLength : -1,
                    });
                    return Promise.reject(error);
                }));
    }

    _fetchCompletePDFBuffer(assetUri, options) {
        let fetchCompletePDFBufferPromise;

        // TODO: using cancellable promise so that the promise chain is not traversed once the asset is no longer
        // the current asset, Need to figure out a better way to handle this situation.

        if (RenditionAPI._isExternalAsset(options)) {
            fetchCompletePDFBufferPromise =
                renditionMakeCancellablePromise(this._externalGetDownloadUri(assetUri, options))
                    .catch(error => {
                        logEvent("GetDownloadUri failed", {
                            assetType: "external",
                            error: JSON.stringify(error),
                        });
                        return Promise.reject(error);
                    })
                    .then(result => (
                        renditionMakeCancellablePromise(this._externalGetContentBuffer(assetUri, result))
                            .catch(error => {
                                logEvent("GetContentBuffer failed", {
                                    assetType: "external",
                                    error: JSON.stringify(error),
                                });
                                return Promise.reject(error);
                            })
                    ));
        } else {
            fetchCompletePDFBufferPromise = renditionMakeCancellablePromise(this._isPDFAsset(assetUri, options))
                .catch(error => {
                    logEvent("Non PDF asset in AJS flow", {
                        assetType: "non-external",
                        error: JSON.stringify(error),
                    });
                    return Promise.reject(error);
                })
                .then(() => renditionMakeCancellablePromise(this._getDownloadUri(assetUri, options))
                    .catch(error => {
                        logEvent("GetDownloadUri failed", {
                            assetType: "non-external",
                            error: JSON.stringify(error),
                        });
                        return Promise.reject(error);
                    })
                    .then(result => {
                        this._perfTimings.ajs.pdfDownloadStart = recordNow(constants.perfMarkers.PDF_DOWNLOAD_START);
                        logEvent("pdfDownloadStartTime", {
                            pdfDownloadStart: this._perfTimings.ajs.pdfDownloadStart,
                        });
                        return renditionMakeCancellablePromise(this._dcapiGetContentBuffer(assetUri, result, options))
                            .catch(error => {
                                logEvent("GetContentBuffer failed", {
                                    assetType: "non-external",
                                    error: JSON.stringify(error),
                                });
                                return Promise.reject(error);
                            });
                    }));
        }

        return fetchCompletePDFBufferPromise.then(buf => {
            if (RenditionAPI._isExternalAsset(options)) {
                // for external assets get actual download times from dc-view-sdk
                const downloadTimes = this._external.apis.file.getDownloadTimes();
                this._perfTimings.ajs.pdfDownloadStart =
                  recordNow(constants.perfMarkers.PDF_DOWNLOAD_START, downloadTimes.pdfDownloadStartTime);
                this._perfTimings.ajs.pdfDownloadEnd =
                  recordNow(constants.perfMarkers.PDF_DOWNLOAD_END, downloadTimes.pdfDownloadEndTime);
                logEvent("pdfDownloadStartTime", { pdfDownloadStart: this._perfTimings.ajs.pdfDownloadStart });
                this._getFileSize(assetUri, options).then(size => {
                    this._logPdfDownloadTotalTime(size);
                }).catch(() => {
                    this._logPdfDownloadTotalTime(0);
                });
            } else {
                this._perfTimings.ajs.pdfDownloadEnd = recordNow(constants.perfMarkers.PDF_DOWNLOAD_END);
                const { pdfDownloadStart, pdfDownloadEnd } = this._perfTimings.ajs;
                const pdfDownloadTotalTime = pdfDownloadEnd - pdfDownloadStart;
                logEvent("pdfDownloadTotalTime", { pdfDownloadTotalTime });
                // reset variable after use to avoid its usage in the next call.
                this._setDownloadUrlType(constants.downloadUrlType.NONE);
            }
            return buf;
        });
    }

    _getDownloadUri(assetUri, options) {
        if (RenditionAPI._isAcpAsset(options)) {
            return this._acpGetMetadata(assetUri, options).then(metadata => metadata.download_url);
        }
        return this._dcapiGetDownloadUri(assetUri, options);
    }

    _getFileSize(assetUri, options) {
        return this._externalGetMetadata(assetUri, options).then(content => {
            if (content.size) {
                return content.size;
            }
            return 0;
        });
    }

    _logPdfDownloadTotalTime(fileSize) {
        const { pdfDownloadStart, pdfDownloadEnd } = this._perfTimings.ajs;
        const pdfDownloadTotalTime = pdfDownloadEnd - pdfDownloadStart;
        logEvent("pdfDownloadTotalTime", {
            pdfDownloadTotalTime,
            fileSize,
            isS3Url: this._isS3DownloadUrl,
        });
        // reset variable after use to avoid its usage in the next call.
        this._setDownloadUrlType(constants.downloadUrlType.NONE);
    }

    /**
     * Gets metadata of file via AJS.
     * Returns Promise which resolves when metadata is available
     *
     * @private
     */
    _ajsGetMetadata(assetUri, options) {
        const optionsCopy = options ? { ...options } : {};
        const documentIdHashPromise = renditionMakeCancellablePromise(getSHA256Digest(assetUri));
        return documentIdHashPromise.then(hashValue => {
            setDocumentId(hashValue);
            this._documentId = hashValue;
        }).catch(() => {
            this._documentId = "";
        }).then(() => {
            if (optionsCopy.forceCacheless !== true &&
                this._assetRenditions[assetUri] &&
                this._assetRenditions[assetUri].ajs &&
                this._assetRenditions[assetUri].ajs.metadataPromise) {
                return this._assetRenditions[assetUri].ajs.metadataPromise;
            }

            const ajsMetadataPromise = this._ajsPostMetaData(assetUri, optionsCopy);

            // handled caching for metaDataAndRendition API separately
            if (!options.enableMetaDataAndRenditionAPI) {
                this._cacheRendition(assetUri, {
                    ajs: {
                        metadataPromise: ajsMetadataPromise,
                    },
                });
            }

            return ajsMetadataPromise;
        });
    }

    /**
     * Gets metadata of file via External.
     * Returns Promise which resolves when metadata is available
     *
     * @private
     */
    _externalGetMetadata(assetUri, options) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].external ||
            !this._assetRenditions[assetUri].external.metadata ||
            options.forceCacheless === true) {
            if (this._externalInitPromise()) {
                this._externalInit();
            }
            return this.externalPromise.then(() =>
                this._external.getSelectedFilesListFromFileIds([assetUri]).then(content => {
                    this._cacheRendition(assetUri, {
                        external: {
                            metadata: content[0],
                        },
                    });
                    return content[0];
                }));
        }
        return Promise.resolve(this._assetRenditions[assetUri].external.metadata);
    }

    /**
     * Gets download uri of file via External.
     * Returns Promise which resolves when metadata is available
     *
     * @private
     */
    _externalGetDownloadUri(assetUri, options) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].external ||
            !this._assetRenditions[assetUri].external.downloadUri ||
            options.forceCacheless === true) {
            return this._externalGetMetadata(assetUri, options).then(content => {
                if (content.downloadUrl) {
                    this._cacheRendition(assetUri, {
                        external: {
                            downloadUri: content.downloadUrl,
                        },
                    });
                    return content.downloadUrl;
                }
                return Promise.reject(new Error("Download Uri not available"));
            });
        }
        return Promise.resolve(this._assetRenditions[assetUri].external.downloadUri);
    }

    /**
     * Gets content buffer for a given set of ranges via external.
     * Returns Promise which resolves with file content buffer
     * when it is available or the cached one
     *
     * @private
     */
    _fetchPDFBufferRanges = (assetUri, opts, ranges) => {
        // eslint-disable-next-line max-len
        const isCompletePDFBufferRequested = ranges.length === 1 && ranges[0].start === 0 && ranges[0].end === undefined;
        if (isCompletePDFBufferRequested) {
            return this._fetchCompletePDFBufferPromise.then(buf => [buf]);
        }

        if (this._externalInitPromise()) {
            this._externalInit();
        }
        return this.externalPromise.then(() => this._externalGetDownloadUri(assetUri, opts).then(downloadUri =>
            this._external.fetchPDFBufferRanges(downloadUri, ranges)));
    }

    /**
     * Gets pdf file size via external.
     * Returns Promise which resolves with file size
     *
     * @private
     */
    _fetchPDFSize = (assetUri, options) => {
        if (this._externalInitPromise()) {
            this._externalInit();
        }
        return this.externalPromise.then(() =>
            this._externalGetDownloadUri(assetUri, options).then(downloadUri =>
                this._external.fetchPDFSize(downloadUri)));
    }

    /**
     * Gets content buffer of a given file URI.
     * fileURI should be an independent URL capable of fetching that
     * file via a GET request.
     * Returns Promise which resolves with file content buffer
     * when it is available or the cached one
     *
     * @private
     */
    _externalGetContentBuffer(assetUri, downloadUrl) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].external ||
            !this._assetRenditions[assetUri].external.contentBuffer) {
            if (this._externalInitPromise()) {
                this._externalInit();
            }
            return this.externalPromise.then(() =>
                this._external.getFileContents(downloadUrl).then(buffer => {
                    this._cacheRendition(assetUri, {
                        external: {
                            contentBuffer: buffer,
                        },
                        ajs: {
                            contentBuffer: buffer,
                        },
                    });
                    return buffer;
                }));
        }
        return Promise.resolve(this._assetRenditions[assetUri].external.contentBuffer);
    }

    /**
     * Gets content rendition of a given connector URI, page and size.
     * Returns Promise which resolves with rendition
     * when it is available or the cached one
     *
     * @private
     */
    _connectorGetRendition(assetUri, options) {
        const size = options.size || "3200";
        const pageNum = 0;
        const k = `${assetUri}-${pageNum}`;
        if (options.forceCacheless === true ||
            !this._assetPageRenditions[k] ||
            !this._assetPageRenditions[k].connector ||
            !this._assetPageRenditions[k].connector.pageImgBuffer ||
            this._assetPageRenditions[k].connector.size !== size ||
            this._assetPageRenditions[k].connector.page !== pageNum) {
            const urlParams = new URLSearchParams(window.location.search);
            const connectorType = constants.CONNECTOR_TYPES_BY_ASSET_TYPE[options.assetType];
            if (connectorType) {
                return serviceAPI["storage-connectors"]()
                    .then(s => s.getStorageProvider(connectorType).then(storageProvider => {
                        let connectorId = urlParams.get("connectorid");
                        if (!connectorId) {
                            connectorId = options.connectorId;
                        }
                        return storageProvider.getRendition(
                            assetUri,
                            s.getConnectorById(connectorId),
                            { size },
                        ).then(data => {
                            const output = {
                                params: {
                                    pageImgBuffer: data,
                                    mimeType: "image/png",
                                    pageNum,
                                },
                            };
                            this._cachePageRendition(assetUri, pageNum, {
                                connector: {
                                    pageImgBuffer: data,
                                    size,
                                    page: pageNum,
                                },
                            });
                            return output;
                        });
                    })).catch(errorResponse => {
                        throw new NoRenditionError((errorResponse && errorResponse.message) || "Unknown");
                    });
            }
            return Promise.reject(new Error("Not supported"));
        }
        const d = this._assetPageRenditions[k].connector;
        const output = {
            params: {
                pageImgBuffer: d.pageImgBuffer,
                mimeType: "image/png",
                pageNum: d.page,
            },
        };
        return Promise.resolve(output);
    }

    // eslint-disable-next-line class-methods-use-this
    _acpGetStorageProvider() {
        return webPlatform.locateImplementation("IStorage", "wp-acp-storage");
    }

    _acpGetRendition(assetUri, options) {
        const size = options.size || "3200";
        const format = options.format || "png";
        const pageNum = options.pageNum !== undefined ? options.pageNum : 1;
        const type = options.type || "image/png";
        const k = `${assetUri}-${pageNum}`;
        if (options.forceCacheless === true ||
            !this._assetPageRenditions[k] ||
            !this._assetPageRenditions[k].acp ||
            !this._assetPageRenditions[k].acp.pageImgBuffer ||
            this._assetPageRenditions[k].acp.format !== format ||
            this._assetPageRenditions[k].acp.size !== size ||
            this._assetPageRenditions[k].acp.page !== pageNum) {
            return new Promise((resolve, reject) => {
                this._acpGetStorageProvider().then(acpStorage => {
                    acpStorage.getRendition(assetUri, {}, {
                        format,
                        pageNum,
                        size,
                        type,
                    }).then(buffer => {
                        const output = {
                            params: {
                                pageImgBuffer: buffer,
                                mimeType: type,
                                pageNum,
                            },
                        };
                        this._cachePageRendition(assetUri, pageNum, {
                            acp: {
                                pageImgBuffer: buffer,
                                mimeType: type,
                                size,
                                page: pageNum,
                                format,
                            },
                        });
                        resolve(output);
                    }).catch(reject);
                }).catch((reject));
            });
        }
        const d = this._assetPageRenditions[k].acp;
        const output = {
            params: {
                pageImgBuffer: d.pageImgBuffer,
                mimeType: d.mimeType,
                pageNum: d.page,
            },
        };
        return Promise.resolve(output);
    }

    /**
     * Gets content metadata of a given connector URI.
     * Returns Promise which resolves with metadata
     * when it is available or the cached one
     *
     * @private
     */
    _connectorGetMetadata(assetUri, options) {
        const assetKey = `${assetUri}`;
        if (options.forceCacheless !== true && this._getAssetFileInfoPromise[assetKey]) {
            return this._getAssetFileInfoPromise[assetKey];
        }
        if (options.forceCacheless === true || !this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].connector ||
            !this._assetRenditions[assetUri].connector.metaData) {
            const urlParams = new URLSearchParams(window.location.search);
            const connectorType = constants.CONNECTOR_TYPES_BY_ASSET_TYPE[options.assetType];
            if (connectorType) {
                this._getAssetFileInfoPromise[assetKey] = serviceAPI["storage-connectors"]()
                    .then(s => s.getStorageProvider(connectorType).then(storageProvider => {
                        const connectorId = options.connectorId || urlParams.get("connectorid");
                        let connector;
                        if (options.cloudUserId) {
                            connector = s.getConnectorsByCloudUserId(options.cloudUserId)
                                ?.find(item => item?.connectorType === connectorType);
                        } else {
                            connector = s.getConnectorById(connectorId);
                        }
                        return storageProvider.getItem(
                            assetUri,
                            connector,
                        ).then(result => {
                            const normalizedItem =
                                storageProvider.normalizeItem(result, connector);
                            if (normalizedItem.type !== "application/pdf") {
                                normalizedItem.numPages = 1;
                                normalizedItem.page_count = 1;
                            }
                            this._cacheRendition(assetUri, {
                                connector: {
                                    metaData: normalizedItem,
                                },
                                ajs: {
                                    metaData: normalizedItem,
                                },
                            });
                            return normalizedItem;
                        })
                            .catch(() => {
                                delete this._getAssetFileInfoPromise[assetKey];
                            });
                    }));
            } else {
                this._getAssetFileInfoPromise[assetKey] = Promise.reject(new Error("Not supported"));
            }
        } else {
            this._getAssetFileInfoPromise[assetKey] =
                Promise.resolve(this._assetRenditions[assetUri].connector.metaData);
        }
        return this._getAssetFileInfoPromise[assetKey];
    }

    _acpGetMetadata(assetUri, options) {
        const assetKey = `${assetUri}`;

        if (options.forceCacheless !== true && this._getAssetFileInfoPromise[assetKey]) {
            return this._getAssetFileInfoPromise[assetKey];
        }

        if (options.forceCacheless === true ||
            !this._assetRenditions[assetKey] ||
            !this._assetRenditions[assetKey].acp
        ) {
            this._getAssetFileInfoPromise[assetKey] = new Promise((resolve, reject) => {
                this._acpGetStorageProvider().then(acpStorage => {
                    acpStorage.getAssetMetadata(assetKey).then(metaDataFromAPI => {
                        metaDataFromAPI.type = metaDataFromAPI.mimeType;
                        this._cacheRendition(assetKey, {
                            acp: {
                                metaData: metaDataFromAPI,
                            },
                        });
                        resolve(metaDataFromAPI);
                    }).catch(err => {
                        delete this._getAssetFileInfoPromise[assetKey];
                        reject(err);
                    });
                }).catch(reject);
            });
        } else {
            const cachedAsset = this._assetRenditions[assetKey].acp;
            this._getAssetFileInfoPromise[assetKey] = Promise.resolve(cachedAsset?.metaData);
        }

        return this._getAssetFileInfoPromise[assetKey];
    }

    /**
     * Gets content buffer of a given connector URI.
     * Returns Promise which resolves with file content buffer
     * when it is available or the cached one
     *
     * @private
     */
    _connectorGetContentBuffer(assetUri, options) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].connector ||
            !this._assetRenditions[assetUri].connector.contentBuffer) {
            const urlParams = new URLSearchParams(window.location.search);
            const connectorType = constants.CONNECTOR_TYPES_BY_ASSET_TYPE[options.assetType];
            if (connectorType) {
                return serviceAPI["storage-connectors"]()
                    .then(s => s.getStorageProvider(connectorType).then(storageProvider => {
                        const connectorId = options.connectorId || urlParams.get("connectorid");
                        return storageProvider.getFileContent(
                            assetUri,
                            s.getConnectorById(connectorId),
                        ).then(buffer => {
                            this._cacheRendition(assetUri, {
                                connector: {
                                    contentBuffer: buffer,
                                },
                                ajs: {
                                    contentBuffer: buffer,
                                },
                            });
                            return buffer;
                        });
                    }));
            }
            return Promise.reject(new Error("Not supported"));
        }
        return Promise.resolve(this._assetRenditions[assetUri].connector.contentBuffer);
    }

    /**
     * Gets rendition URI of file via DCAPI.
     * Returns Promise which resolves when rendition uri is available
     *
     * @private
     */
    _dcapiGetRenditionUri(assetUri) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.renditionUri) {
            return this._dcapi.call(
                "assets.rendition_uri",
                {
                    resource_parameter: this._dcapiGetAssetUri(assetUri),
                    accept: "asset_uri_rendition_v1.json",
                    headers: { "x-request-id": uuid.v4() },
                },
            ).then(
                result => {
                    this._cacheRendition(assetUri, {
                        dcapi: {
                            renditionUri: result.content.uri,
                        },
                    });
                    return result.content.uri;
                },
                err => Promise.reject(err),
            );
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.renditionUri);
    }

    /**
     * Gets rendition of page for a given renditionURI via External Provider.
     * Returns Promise which resolves with rendition content
     * when it is available
     *
     * @private
     */
    _externalGetRendition(assetUri, options) {
        const pageNum = options.pageNum !== undefined ? options.pageNum : 1;
        const k = `${assetUri}-${pageNum}`;
        if (!this._assetPageRenditions[k] ||
            !this._assetPageRenditions[k].external ||
            !this._assetPageRenditions[k].external.rendition ||
            !this._assetPageRenditions[k].external.rendition[pageNum] ||
            options.forceCacheless === true) {
            if (this._externalInitPromise()) {
                this._externalInit();
            }
            return this.externalPromise.then(() => {
                if (typeof this._external.getRenditionByFileId !== "function") {
                    return Promise.reject(new Error("Cannot get External rendition"));
                }
                return this._external.getRenditionByFileId(assetUri, options).then(renditionResponse => {
                    const pageObj = {
                        external: {
                            rendition: {},
                        },
                    };
                    pageObj.external.rendition[pageNum] = {
                        params: renditionResponse,
                    };
                    this._cachePageRendition(assetUri, pageNum, pageObj);
                    return Promise.resolve({
                        params: renditionResponse,
                    });
                }, err => Promise.reject(err));
            });
        }
        return Promise.resolve(this._assetPageRenditions[k].external.rendition[pageNum]);
    }


    /**
     * Gets direct rendition of page for a given asset via DCAPI.
     * Returns Promise which resolves with rendition content
     * when it is available
     *
     * @private
     */
    _dcapiGetDirectRenditionForPage(assetUri, options) {
        const size = options.size || "1200";
        const format = options.format || "jpg";
        const pageNum = options.pageNum !== undefined ? options.pageNum : 1;
        const k = `${assetUri}-${pageNum}`;
        const r = this._assetPageRenditions[k];
        let d;
        if (r) {
            d = r.dcapi;
        }
        if (d &&
            d.pageImgBuffer &&
            d.format === format &&
            d.size === size &&
            d.page === pageNum) {
            const output = {
                params: {
                    pageImgBuffer: d.pageImgBuffer,
                    mimeType: d.mimeType,
                    pageNum: d.page,
                },
            };
            return Promise.resolve(output);
        }
        return this._dcapi.call(
            "assets.rendition_direct",
            {
                accept: "image/*",
                resource_parameter: RenditionAPI._dcapiGetAssetUrn(assetUri),
                uri_parameters: {
                    size,
                    format,
                    page: pageNum - 1,
                    processing_hint: "speed_over_quality",
                },
                headers: { "x-request-id": uuid.v4() },
                responseType: "arraybuffer",
            },
        ).then(
            result => {
                const output = {
                    params: {
                        pageImgBuffer: result.content,
                        mimeType: result.headers["content-type"],
                        pageNum,
                    },
                };
                this._cachePageRendition(assetUri, pageNum, {
                    dcapi: {
                        pageImgBuffer: result.content,
                        mimeType: result.headers["content-type"],
                        size,
                        page: pageNum,
                        format,
                    },
                });
                return Promise.resolve(output);
            },
            err => Promise.reject(err),
        );
    }

    /**
     * Gets rendition of page for a given assetURI via DCAPI.
     * Will retry up to DCAPI_PDF_RENDITION_MAX_RETRIES times if resource is not found.
     * Returns Promise which resolves with rendition content
     * when it is available
     *
     * @private
     */
    _dcapiGetRendition(assetUri, options, tryCount = 1) {
        let renditionPromise;
        const startTimer = performance.now();
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            renditionPromise = this._reviewGetRendition(assetUri, options);
        } else if (RenditionAPI._isPDFNowAsset(options)) {
            renditionPromise = this._pdfnowGetRendition(assetUri, options);
        } else if (RenditionAPI._isConnectorAsset(options)) {
            renditionPromise = this._connectorGetRendition(assetUri, options);
        } else if (RenditionAPI._isAcpAsset(options)) {
            renditionPromise = this._acpGetRendition(assetUri, options);
        } else {
            renditionPromise = this.dcapiPromise.then(() => (
                this._dcapiGetDirectRenditionForPage(assetUri, options)
            ));
        }

        return renditionPromise.catch(error => {
            const { code } = error;
            if (code === 404 || code === 429) {
                const isShared = RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options);
                const maxRetries = isShared ? 0 : (options?.maxRetries || constants.DCAPI_PDF_RENDITION_MAX_RETRIES);
                if (tryCount < maxRetries) {
                    const delayTime = options?.delayTime || constants.RETRY_INTERVAL;
                    return delay(delayTime).then(() =>
                        this._dcapiGetRendition(assetUri, options, tryCount + 1));
                }
                const { format, size, type } = options;
                logEvent("dcapiGetRendition max retries", {
                    asset_uri: assetUri,
                    code,
                    format,
                    level: "error",
                    rendition_duration: performance.now() - startTimer,
                    size,
                    type,
                });
            }
            throw error;
        });
    }


    /**
     * Will retry up to DCAPI_PDF_RENDITION_MAX_RETRIES times if resource is not found for review rendition type.
     * Returns Promise which resolves with rendition content
     * when it is available
     *
     * @private
     */
    _getReviewRenditionWithRetries(assetUri, options, tryCount = 1) {
        const startTimer = performance.now();
        const renditionPromise = this._reviewGetRendition(assetUri, options);
        return renditionPromise.catch(error => {
            const { code } = error;
            if (code === 404 || code === 429) {
                if (tryCount < constants.DCAPI_PDF_RENDITION_MAX_RETRIES) {
                    return delay(constants.RETRY_INTERVAL).then(() =>
                        this._getReviewRenditionWithRetries(assetUri, options, tryCount + 1));
                }
                const { format, size, type } = options;
                logEvent("getReviewRenditionWithRetries max retries", {
                    asset_uri: assetUri,
                    code,
                    format,
                    level: "error",
                    rendition_duration: performance.now() - startTimer,
                    size,
                    type,
                });
            }
            throw error;
        });
    }

    /**
     * Gets document rendition of a given assetURI via DCAPI.
     * options should have a 'format' parameter otherwise "owp" is used
     * as a default.
     * Returns Promise which resolves with uri for document rendition
     *
     * @private
     */
    _dcapiGetDocumentRenditionUri(assetUri, opts) {
        // Get document rendition URI for this asset via DCAPI
        const options = opts;
        return this.dcapiPromise.then(() => {
            const format = options.format || "owp";
            if (!this._assetRenditions[assetUri] ||
                !this._assetRenditions[assetUri].dcapi ||
                !this._assetRenditions[assetUri].dcapi.documentRenditionUri) {
                return this._dcapi.call(
                    "assets.rendition_uri",
                    {
                        resource_parameter: this._dcapiGetAssetUri(assetUri),
                        accept: "asset_uri_rendition_v1.json",
                        // We need ticket-based authentication so that we can use the resulting
                        // OWP URL as a src attribute
                        uri_parameters: {
                            make_ticket: format === "owp",
                            type: "document",
                        },
                        headers: { "x-request-id": uuid.v4() },
                    },
                ).then(
                    result => {
                        this._cacheRendition(assetUri, {
                            dcapi: {
                                documentRenditionUri: result.content.uri,
                            },
                        });
                        return this._assetRenditions[assetUri].dcapi.documentRenditionUri +
                            (options.format ? (`&format=${options.format}`) : "");
                    },
                    err => Promise.reject(err),
                );
            }
            return Promise.resolve(this._assetRenditions[assetUri].dcapi.documentRenditionUri +
                (options.format ? (`&format=${options.format}`) : ""));
        });
    }

    /**
     * Gets document rendition of a given assetURI via DCAPI.
     * options should have a 'format' parameter otherwise "owp" is used
     * as a default.
     * Returns Promise which resolves with uri for document rendition
     *
     * @private
     */
    _dcapiGetDocumentRendition(assetUri, opts) {
        // Get document rendition for this asset via DCAPI
        const options = opts;
        return this.dcapiPromise.then(() => {
            const format = options.format || "owp";
            options.format = undefined;
            return this._dcapiGetDocumentRenditionUri(assetUri, options).then(documentRenditionUri => this._dcapi.call(
                "assets.document_rendition",
                {
                    accept: "*/*",
                    resource_parameter: documentRenditionUri,
                    uri_parameters: {
                        format,
                        fallback: "none",
                    },
                    headers: { "x-request-id": uuid.v4() },
                    responseType: "arraybuffer",
                },
            ).then(result => result.content));
        });
    }

    /**
     * Gets document rendition status of a given assetURI via DCAPI.
     * Returns Promise which resolves with status object.
     *
     * @private
     */
    _dcapiGetDocumentRenditionStatus(assetUri) {
        // Get document rendition status for this asset via DCAPI
        return this.dcapiPromise.then(() => this._dcapi.call(
            "assets.document_rendition_status",
            {
                resource_parameter: this._dcapiGetAssetUri(assetUri),
                accept: "asset_document_rendition_status_v1.json",
                headers: { "x-request-id": uuid.v4() },
            },
        ).then(
            result => result.content.document_rendition_status,
            err => Promise.reject(err),
        ));
    }

    /**
     * Gets download url for a review asset.
     * Returns Promise which resolves with download uri
     * when it is available
     * @private
     */
    _reviewGetDownloadUri(assetUri, isACP = false) {
        this._initReviewProviderIfRequired();
        const promise = new Promise((resolve, reject) => {
            this.reviewApiPromise.then(() => {
                this._reviewApi.initReviewIfRequired().then(() => {
                    const review = this._reviewApi.getReview(assetUri);
                    let shouldFetchS3Url = true;
                    if (review !== undefined) {
                        const downloadUriStartTime = recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_START);
                        const getFileDownloadURLPromise = isACP
                            ? review.getFileDownloadURLByAssetUri(assetUri, "view", shouldFetchS3Url)
                            : review.getFileDownloadURL("view", shouldFetchS3Url);
                        getFileDownloadURLPromise.then(downloadUri => {
                            this._setDownloadUrlType(constants.downloadUrlType.S3);
                            resolve(downloadUri);
                            const downloadUriEndTime = recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_END);
                            logEvent("pdfDownloadUriTotalTime", {
                                pdfDownloadUriTotalTime: downloadUriEndTime - downloadUriStartTime,
                            });
                        }).catch(() => {
                            shouldFetchS3Url = false;
                            // fetch dcapi url if fail to fetch direct s3 url
                            const getFileDownloadURLPromiseRetry = isACP
                                ? review.getFileDownloadURLByAssetUri(assetUri, "view", shouldFetchS3Url)
                                : review.getFileDownloadURL("view", shouldFetchS3Url);
                            return getFileDownloadURLPromiseRetry.then(downloadUri => {
                                this._setDownloadUrlType(constants.downloadUrlType.DCAPI);
                                resolve(downloadUri);
                                const downloadUriEndTime = recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_END);
                                logEvent("pdfDownloadUriTotalTime", {
                                    pdfDownloadUriTotalTime: downloadUriEndTime - downloadUriStartTime,
                                });
                            }).catch(err => {
                                this._setDownloadUrlType(constants.downloadUrlType.NONE);
                                logEvent("Failed to fetch reviewDownloadUri", JSON.stringify(err));
                            });
                        });
                    } else {
                        reject(new Error("Review does not exist"));
                    }
                });
            });
        });
        return promise;
    }

    _setDownloadUrlType(value) {
        this._downloadUrlType = value;
    }

    get _isS3DownloadUrl() {
        return this._downloadUrlType === constants.downloadUrlType.S3;
    }

    _reviewGetMetadata(assetUri) {
        this._initReviewProviderIfRequired();
        const promise = new Promise((resolve, reject) => {
            this.reviewApiPromise.then(() => {
                this._reviewApi.initReviewIfRequired().then(() => {
                    const review = this._reviewApi.getReview(assetUri);
                    if (review !== undefined) {
                        review.getSendBootstrapData().then(data => {
                            const metadata = {};
                            if (data && data.meta_list && data.meta_list instanceof Array &&
                                data.meta_list.length > review.getDownloadAssetIndex()) {
                                const metaList = data.meta_list[review.getDownloadAssetIndex()];
                                metadata.type = metaList.content_type;
                                metadata.assetMimeType = metaList.content_type;
                                metadata.name = metaList.name;
                                metadata.last_pagenum = metaList.last_pagenum || 0;
                                metadata.numPages = metaList.page_count || 0;
                                metadata.size = metaList.size;
                                metadata.id = metaList.id;
                                this._cacheRendition(assetUri, {
                                    dcapi: {
                                        contentData: cloneDeep(metadata),
                                    },
                                });
                                resolve(metadata);
                            } else {
                                reject(new Error("Invalid Bootstrap data"));
                            }
                        }).catch(error => {
                            reject(error);
                        });
                    } else {
                        reject(new Error("Review does not exist"));
                    }
                });
            }).catch(reject);
        });
        return promise;
    }

    _reviewGetRenditionAuthHeader(fetchOptions) {
        const outOptions = fetchOptions;
        if (this._dcapi &&
            this._dcapi.session &&
            this._dcapi.session.content &&
            this._dcapi.session.content.access_token) {
            outOptions.headers = {
                Authorization: `Bearer ${this._dcapi.session.content.access_token}`,
            };
        }
        return outOptions;
    }

    _getReviewRenditionURL(assetUri, options) {
        const pageNum = options.pageNum !== undefined ? (options.pageNum - 1) : 0;
        const nsize = parseInt(options.size || "1200", 10);

        // enabled the DCAPI renditions call for review files
        // if (nsize > 500 && RenditionAPI._isReviewAsset(options)) { return Promise.reject(); }

        const promise = new Promise((resolve, reject) => {
            this.reviewApiPromise.then(() => {
                this._reviewApi.instantiateReview().then(() => {
                    const review = this._reviewApi.getReview(assetUri);
                    if (review !== undefined) {
                        if (assetUri) {
                            this._reviewApi.getAllBaseUris().then(baseUris => {
                                const sendUri = baseUris.send_api;
                                const invitationBaseUri = sendUri.replace("/api/", "/invitation/");
                                let invitationUrl = `${invitationBaseUri + encodeURIComponent(assetUri)
                                }/asset/asset-0`;

                                let renditionClass;
                                switch (true) {
                                case (nsize <= 30):
                                    renditionClass = "/icon";
                                    break;
                                case (nsize <= 120):
                                    renditionClass = "/thumbnail";
                                    break;
                                case (nsize <= 500):
                                    renditionClass = "/tile";
                                    break;
                                default:
                                    renditionClass = "/preview";
                                    break;
                                }
                                invitationUrl += renditionClass;
                                if (pageNum) { invitationUrl += `?page=${pageNum}`; }
                                resolve(invitationUrl);
                            }, reject);
                        } else {
                            reject(new Error("Invalid Bootstrap data"));
                        }
                    } else {
                        reject(new Error("Review does not exist"));
                    }
                }, reject);
            }).catch(reject);
        });
        return promise;
    }

    _getCachedRedirectURL(opts, invalidateCache) {
        const {
            assetUri, pageNum, renditionUrl, fetchOptions,
        } = opts;
        return new Promise((resolve, reject) => {
            const { pageNum: cachedPageNum, cachedAssetUri } = this._cachedRedirectUrl;
            let { redirectUrl: cachedRedirectUrl } = this._cachedRedirectUrl;
            const cacheIsNotEmpty = "cachedAssetUri" in this._cachedRedirectUrl;
            const isPageRendition = renditionUrl.includes("/preview");
            if ((cacheIsNotEmpty && cachedAssetUri === assetUri) && isPageRendition && !invalidateCache) {
                if (cachedPageNum === pageNum) {
                    resolve(cachedRedirectUrl);
                }
                if (cachedRedirectUrl.includes("page=")) {
                    cachedRedirectUrl = cachedRedirectUrl.replace(/page=\d+/g, `page=${pageNum - 1}`);
                } else {
                    cachedRedirectUrl = `${cachedRedirectUrl}&page=${pageNum - 1}`;
                }
                resolve(cachedRedirectUrl);
            } else {
                RenditionAPI._fetchWithCredentials(renditionUrl, fetchOptions).then(resp => {
                    let redirectUrl;
                    if (resp && resp.status === 200 && resp.content) {
                        const content = JSON.parse(resp.content);
                        redirectUrl = content.url;
                        if (isPageRendition) {
                            this._cachedRedirectUrl = {
                                cachedAssetUri: assetUri,
                                redirectUrl,
                                pageNum,
                            };
                        }
                        resolve(redirectUrl);
                    }
                    if (!redirectUrl) {
                        reject(new Error("Redirect url could not be obtained"));
                    }
                }).catch(error => reject(error));
            }
        });
    }

    _getRenditionsUsingCachedRedirectURL(opts, invalidateCache = false, retries = 0) {
        return new Promise((resolve, reject) => {
            this._getCachedRedirectURL(opts, invalidateCache).then(redirectUrl => {
                resolve(this._getRenditionsUsingRedirectURL(redirectUrl, opts, retries));
            }).catch(error => {
                reject(error);
            });
        });
    }

    _getRenditionsUsingRedirectURL(redirectUrl, opts, retries = 0) {
        const {
            assetUri, pageNum, size, renditionUrl, pageRenditionPromise,
        } = opts;
        let options = {
            responseType: "arraybuffer",
            withCredentials: this.options.fetchWithCredentials,
        };
        options = this._reviewGetRenditionAuthHeader(options);
        if (!options.headers) {
            options.headers = {};
        }
        return new Promise((resolve, reject) => {
            if (pageRenditionPromise) {
                pageRenditionPromise.then(res => {
                    const output = {
                        params: {
                            pageImgBuffer: res.buffer,
                            mimeType: res.mimeType,
                            pageNum,
                        },
                    };
                    this._cachePageRendition(assetUri, pageNum, {
                        dcapi: {
                            renditionUri: renditionUrl,
                            pageImgBuffer: res.buffer,
                            mimeType: res.mimeType,
                            size,
                            page: pageNum,
                        },
                    });
                    resolve(output);
                }).catch(error => {
                    reject(error);
                });
            } else {
                RenditionAPI._fetchWithCredentials(redirectUrl, options).then(result => {
                    const output = {
                        params: {
                            pageImgBuffer: result.content,
                            mimeType: result.headers["content-type"],
                            pageNum,
                        },
                    };
                    this._cachePageRendition(assetUri, pageNum, {
                        dcapi: {
                            renditionUri: renditionUrl,
                            pageImgBuffer: result.content,
                            mimeType: result.headers["content-type"],
                            size,
                            page: pageNum,
                        },
                    });
                    const error = result && (result.status === 410 || result.status === 401);
                    if (error && retries < DCAPI_REDIRECT_URL_MAX_RETRIES) {
                        // Retry downloading renditions until max retires
                        this._getRenditionsUsingRedirectURL(redirectUrl, opts, retries + 1);
                    }
                    resolve(output);
                }).catch(error => {
                    reject(error);
                });
            }
        });
    }

    _reviewGetRendition(assetUri, options) {
        this._initReviewProviderIfRequired();
        const assetURLPromise = window.adobe_dc_sdk.cache.assetURLPromise &&
            window.adobe_dc_sdk.cache.assetURLPromise[assetUri];
        const pageNum = options.pageNum !== undefined ? options.pageNum : 1;
        const size = options.size || "1200";
        const key = `${assetUri}-${pageNum}-${size}`;
        const pageRenditionPromise = options.pageRenditionPromise;
        if (!this._assetPageRenditionPromises[key]) {
            this._assetPageRenditionPromises[key] = new Promise((resolve, reject) => {
                if (assetURLPromise) {
                    const opts = {
                        assetUri, pageNum, size, pageRenditionPromise,
                    };
                    assetURLPromise.then(data => {
                        let redirectUrl = data.url;
                        if (redirectUrl.includes("page=")) {
                            redirectUrl = redirectUrl.replace(/page=\d+/g, `page=${pageNum - 1}`);
                        }
                        this._getRenditionsUsingRedirectURL(redirectUrl, opts)
                            .then(output => resolve(output))
                            .catch(error => {
                                delete this._assetPageRenditionPromises[key];
                                delete window.adobe_dc_sdk.cache.assetURLPromise;
                                reject(error);
                            });
                    }).catch(() => {
                        delete this._assetPageRenditionPromises[key];
                        delete window.adobe_dc_sdk.cache.assetURLPromise;
                        return this._reviewGetRendition(assetUri, options);
                    });
                } else {
                    this._getReviewRenditionURL(assetUri, options).then(renditionUrl => {
                        let fetchOptions = {
                            withCredentials: this.options.fetchWithCredentials,
                        };
                        fetchOptions = this._reviewGetRenditionAuthHeader(fetchOptions);
                        if (!fetchOptions.headers) {
                            fetchOptions.headers = {};
                        }
                        Object.assign(
                            fetchOptions.headers,
                            { ACCEPT: "application/vnd.adobe.skybox+json;version=1", "x-api-client-id": "api_browser" },
                        );
                        const k = `${assetUri}-${pageNum}`;
                        if (this._assetPageRenditions[k] &&
                            this._assetPageRenditions[k].dcapi &&
                            this._assetPageRenditions[k].dcapi.pageImgBuffer &&
                            this._assetPageRenditions[k].dcapi.renditionUri === renditionUrl &&
                            this._assetPageRenditions[k].dcapi.size === size &&
                            this._assetPageRenditions[k].dcapi.page === pageNum) {
                            const output = {
                                params: {
                                    pageImgBuffer: this._assetPageRenditions[k].dcapi.pageImgBuffer,
                                    mimeType: this._assetPageRenditions[k].dcapi.mimeType,
                                    pageNum: this._assetPageRenditions[k].dcapi.page,
                                },
                            };
                            resolve(output);
                            return;
                        }
                        const opts = {
                            assetUri, pageNum, size, renditionUrl, fetchOptions,
                        };
                        this._getRenditionsUsingCachedRedirectURL(opts)
                            .then(output => resolve(output))
                            .catch(error => {
                                delete this._assetPageRenditionPromises[key];
                                reject(error);
                            });
                    }).catch(() => {
                        delete this._assetPageRenditionPromises[key];
                        reject(new Error("Review Rendition url could not be created"));
                    });
                }
            });
        }
        return this._assetPageRenditionPromises[key];
    }

    /**
     * Gets downloadURI for a given assetURI via DCAPI
     * Returns Promise which resolves with downloadURI
     *
     * @private
     */
    _dcapiGetDownloadUri(assetUri, options) {
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            return this._reviewGetDownloadUri(assetUri, options);
        }
        if (RenditionAPI._isSignAsset(options)) {
            return Promise.resolve(assetUri);
        }
        if (!this.options.useDCAPI) {
            return Promise.resolve(assetUri);
        }
        const dcapiDownloadUriParams = {
            resource_parameter: this._dcapiGetAssetUri(assetUri),
            accept: "asset_uri_download_v1.json",
            headers: { "x-request-id": uuid.v4() },

        };
        const uriPromise = new Promise((resolve, reject) => {
            this.dcapiPromise.then(() => {
                recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_START);
                return this._dcapi.call(
                    "assets.download_uri",
                    {
                        ...dcapiDownloadUriParams,
                        uri_parameters: {
                            make_direct_storage_uri: true,
                        },
                    },
                ).then(async result => {
                    this._setDownloadUrlType(constants.downloadUrlType.S3);
                    recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_END);
                    const eTagValue = result?.headers?.etag;
                    await updateETagForViewerAssetSelection(assetUri, eTagValue);
                    resolve(result.content.uri);
                }).catch(() =>
                    this._dcapi.call(
                        "assets.download_uri",
                        dcapiDownloadUriParams,
                    ).then(result => {
                        this._setDownloadUrlType(constants.downloadUrlType.DCAPI);
                        recordNow(constants.perfMarkers.PDF_DOWNLOAD_URI_END);
                        resolve(result.content.uri);
                    }).catch(err => {
                        this._setDownloadUrlType(constants.downloadUrlType.NONE);
                        reject(err);
                    }));
            });
        });
        return uriPromise;
    }

    /**
 * Gets user downloadURI for a given assetURI via DCAPI
 * Returns Promise which resolves with downloadURI
 *
 * @private
 */
    _dcapiGetUserDownloadUri(assetUri, options) {
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            return this._reviewGetDownloadUri(assetUri, options);
        }
        if (!this.options.useDCAPI) {
            return Promise.resolve(assetUri);
        }
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.userDownloadUri) {
            return this.dcapiPromise.then(() => this._dcapi.call(
                "assets.download_uri",
                {
                    resource_parameter: this._dcapiGetAssetUri(assetUri),
                    accept: "asset_uri_download_v1.json",
                    headers: { "x-request-id": uuid.v4() },
                },
            ).then(
                result => {
                    this._cacheRendition(assetUri, {
                        dcapi: {
                            userDownloadUri: result.content.uri,
                        },
                    });
                    return result.content.uri;
                },
                err => Promise.reject(err),
            ));
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.userDownloadUri);
    }

    /**
 * Sets last page number metadata of an asset via DCAPI
 * Returns Promise when the update is done
 * @private
 */
    _dcapiSetLastPageNum(assetUri, options) {
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            return Promise.reject(new Error("Cannot set last page number on a review/parcel asset"));
        }
        return this.dcapiPromise.then(() => this._dcapi.call(
            "assets.put_metadata_field",
            {
                resource_parameter: this._dcapiGetAssetUri(assetUri),
                content_type: "asset_metadata_field_v1.json",
                content: {
                    last_pagenum: (options.lastPageNum - 1),
                },
                uri_parameters: {
                    fields: "last_pagenum",
                },
            },
        ));
    }

    /**
 * Sets last access metadata of an asset via DCAPI
 * Returns Promise when the update is done
 * @private
 */
    _dcapiSetLastAccess(assetUri, options) {
        if (RenditionAPI._isReviewAsset(options) || RenditionAPI._isParcelAsset(options)) {
            return Promise.reject(new Error("Cannot set last access on a review/parcel asset"));
        }
        return this.dcapiPromise.then(() => this._dcapi.call(
            "assets.put_metadata_field",
            {
                resource_parameter: this._dcapiGetAssetUri(assetUri),
                content_type: "asset_metadata_field_v1.json",
                content: {
                    last_access: "",
                },
                uri_parameters: {
                    fields: "last_access",
                },
            },
        ));
    }

    /* This method is a duplicate of AdobeDC.http.get but has following customizations:-
    1) enables sending credentials for cross origin requests
    2) Does not add X-requested-with header as it results in a pre-flight request
    yguptaTODO - Replace it with AdobeDC.http.get call once PR there gets merged */
    static _fetchWithCredentials(url, opts) {
        let xhr;
        let timeout;
        const method = "GET";
        let content;
        const options = opts || {};
        timeout = options.timeout;
        if (!(timeout >= 0)) { timeout = AdobeDC.http.timeout; }
        const promise = new Promise(((fulfill, reject) => {
            try {
                xhr = new XMLHttpRequest();
                xhr.open("GET", url, true);
                if (timeout >= 0) { xhr.timeout = timeout; }
                if (options.responseType) { xhr.responseType = options.responseType; }
                if (options.headers) {
                    Object.entries(options.headers).some(([key, val]) =>
                        xhr.setRequestHeader(key, val));
                }
                if (options.withCredentials) {
                    xhr.withCredentials = options.withCredentials;
                }
                xhr.onreadystatechange = () => {
                    if (!options.response_content_open) {
                        // Return if waiting for the entire response and it's not done.
                        if (xhr.readyState !== 4) {
                            return;
                        }
                    } else if (!xhr.readyState || xhr.readyState < 2) {
                        // Return if waiting for response headers and they're not available.
                        return;
                    }
                    let result;
                    try {
                        result = {
                            status: xhr.status,
                            statusText: xhr.statusText || xhr.status,
                            headers: AdobeDC.http.parseHeaders(xhr.getAllResponseHeaders()),
                            content: undefined,
                        };
                        // Process the response content unless told otherwise.
                        if (!options.response_content_open) {
                            if (xhr.responseType && xhr.responseType !== "text") {
                                result.content = xhr.response;
                            } else {
                                result.content = xhr.responseText;
                            }
                        }
                        if (xhr.status > 0 && xhr.status < 400) {
                            fulfill(result);
                        } else {
                            if (xhr.status === 0 && !result.statusText) {
                                result.statusText = "Timeout or network error";
                            }
                            throw new AdobeDC.AdobeError(result.statusText, result.status, result);
                        }
                    } catch (err) {
                        if (err && !(err instanceof Error)) {
                            reject(new AdobeDC.AdobeError(err.stack || err, result.status, result));
                        } else {
                            reject(err);
                        }
                    }
                };
                if (!options.request_content_open) { xhr.send(content); }
            } catch (err) {
                reject(new AdobeDC.AdobeError(err.stack || err, 0, {
                    method,
                    url,
                    options,
                }));
            }
        }));
        if (options.request_content_open || options.response_content_open) {
            promise.request = xhr;
        }
        return promise;
    }

    /**
 * Gets content buffer of a given file URI.
 * fileURI should be an independent URL capable of fetching that
 * file via a GET request.
 * Returns Promise which resolves with file content buffer
 * when it is available or the cached one
 *
 * @private
 */
    _dcapiGetContentBuffer(assetUri, fileUri, opts) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.contentBuffer) {
            const options = {
                responseType: "arraybuffer",
                withCredentials: this.options.fetchWithCredentials,
            };
            if (!RenditionAPI._isSignAsset(opts) && !this._isS3DownloadUrl &&
                this._dcapi &&
                this._dcapi.session &&
                this._dcapi.session.content &&
                this._dcapi.session.content.access_token) {
                options.headers = {
                    Authorization: `Bearer ${this._dcapi.session.content.access_token}`,
                };
            }
            return RenditionAPI._fetchWithCredentials(fileUri, options).then(content => {
                const buffer = content.content;
                this._cacheRendition(assetUri, {
                    ajs: {
                        contentBuffer: buffer,
                        contentFormat: content.headers["x-content-format"],
                    },
                    dcapi: {
                        contentBuffer: buffer,
                        contentFormat: content.headers["x-content-format"],
                    },
                });
                return buffer;
            });
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.contentBuffer);
    }

    /**
 * Gets user content buffer of a given file URI.
 * fileURI should be an independent URL capable of fetching that
 * file via a GET request.
 * Returns Promise which resolves with file content buffer
 * when it is available or the cached one
 *
 * @private
 */
    _dcapiGetUserContentBuffer(assetUri, fileUri) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.userContentBuffer) {
            const options = {
                responseType: "arraybuffer",
                withCredentials: this.options.fetchWithCredentials,
            };
            if (this._dcapi &&
                this._dcapi.session &&
                this._dcapi.session.content &&
                this._dcapi.session.content.access_token) {
                options.headers = {
                    Authorization: `Bearer ${this._dcapi.session.content.access_token}`,
                };
            }
            return RenditionAPI._fetchWithCredentials(fileUri, options).then(content => {
                const buffer = content.content;
                this._cacheRendition(assetUri, {
                    ajs: {
                        userContentBuffer: buffer,
                    },
                    dcapi: {
                        userContentBuffer: buffer,
                    },
                });
                return buffer;
            });
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.userContentBuffer);
    }


    _pdfnowGetMetadata(assetUri, options, tryCount = 1) {
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].pdfnow ||
            !this._assetRenditions[assetUri].pdfnow.contentData ||
            options.forceCacheless === true) {
            if (this._pdfnowInitPromise()) {
                this._pdfnowInit();
            }
            return new Promise((resolve, reject) => {
                this.pdfnowApiPromise.then(() => {
                    this._pdfnowDcapi.call("assets.get_metadata", {
                        accept: "asset_metadata_basic_v1.json",
                        resource_parameter: assetUri,
                    }).then(metadataResults => {
                        const metadataContent = metadataResults.content;
                        metadataContent.assetMimeType = metadataResults.content_type;
                        metadataContent.last_pagenum = metadataResults.content.last_pagenum || 0;
                        let numPages = metadataResults.content.page_count;
                        let p;
                        if (numPages === undefined) {
                            // retry
                            if (tryCount === constants.DCAPI_METADATA_MAX_RETRIES) {
                                numPages = 1;
                            } else {
                                p = this._pdfnowGetMetadata(assetUri, options, tryCount + 1);
                            }
                        }
                        if (!p) {
                            metadataContent.numPages = numPages;
                            this._cacheRendition(assetUri, {
                                pdfnow: {
                                    contentData: cloneDeep(metadataContent),
                                },
                            });
                            p = Promise.resolve(this._assetRenditions[assetUri].pdfnow.contentData);
                        }
                        return p.then(resolve).catch(reject);
                    }).catch(reject);
                }).catch(reject);
            });
        }
        return Promise.resolve(this._assetRenditions[assetUri].pdfnow.contentData);
    }


    _pdfnowGetRendition(assetUri, options) {
        const size = options.size || "3200";
        const format = options.format || "png";
        const pageNum = options.pageNum !== undefined ? options.pageNum : 1;
        const k = `${assetUri}-${pageNum}`;
        if (options.forceCacheless === true ||
            !this._assetPageRenditions[k] ||
            !this._assetPageRenditions[k].pdfnow ||
            !this._assetPageRenditions[k].pdfnow.pageImgBuffer ||
            this._assetPageRenditions[k].pdfnow.format !== format ||
            this._assetPageRenditions[k].pdfnow.size !== size ||
            this._assetPageRenditions[k].pdfnow.page !== pageNum) {
            if (this._pdfnowInitPromise()) {
                this._pdfnowInit();
            }
            return new Promise((resolve, reject) => {
                this.pdfnowApiPromise.then(() => {
                    this._pdfnowDcapi.call("assets.rendition_direct", {
                        accept: "image/*",
                        resource_parameter: assetUri,
                        responseType: "arraybuffer",
                        uri_parameters: {
                            format,
                            page: pageNum - 1,
                            size,
                        },
                    }).then(result => {
                        const output = {
                            params: {
                                pageImgBuffer: result.content,
                                mimeType: result.headers["content-type"],
                                pageNum,
                            },
                        };
                        this._cachePageRendition(assetUri, pageNum, {
                            pdfnow: {
                                pageImgBuffer: result.content,
                                mimeType: result.headers["content-type"],
                                size,
                                page: pageNum,
                                format,
                            },
                        });
                        resolve(output);
                    }).catch(reject);
                }).catch(reject);
            });
        }
        const d = this._assetPageRenditions[k].pdfnow;
        const output = {
            params: {
                pageImgBuffer: d.pageImgBuffer,
                mimeType: d.mimeType,
                pageNum: d.page,
            },
        };
        return Promise.resolve(output);
    }

    _openPdfResponseCallback(assetUri, data, opts) {
        const enableMetaDataAndRenditionAPI = opts?.enableMetaDataAndRenditionAPI;
        if (enableMetaDataAndRenditionAPI && !data.errorCode) {
            const { zoomLevelCalculatorFn, renditionParams: { renditionContainerWidth } } = opts;
            this._assetRenditions[assetUri].ajs.renditionZoomLevel[opts.renditionParams.pageNum] = zoomLevelCalculatorFn(data, { renditionContainerWidth });
        }
        this._assetRenditions[assetUri].ajs.metadataPromiseResolve(data);
    }

    _getRenditionParams = options => {
        const params = Object.assign({
            devicePixelRatio: 1,
        }, options);

        let {
            requestType,
        } = params;

        const { PageRequestType } = AJSEnums;
        if (requestType === "page") requestType = PageRequestType.PAGE;
        else if (requestType === "cache") requestType = PageRequestType.PAGE_CACHE;
        else if (requestType === "thumbnail") requestType = PageRequestType.THUMBNAIL;
        params.requestType = requestType;
        return params;
    }

    _handleRenditionResponse = response => {
        const renditionResponse = Object.assign({}, response);
        const {
            buffer: pageImgBuffer,
            ...rest
        } = renditionResponse.data;
        // make response similar to dcapi rendition
        renditionResponse.params = {
            pageImgBuffer,
            ...rest,
        };
        delete renditionResponse.data;
        return renditionResponse;
    }

    _cacheRenditionPromise(assetUri, ajsRenditionPromise, options) {
        if (!this._assetRenditions[assetUri].ajs.renditionPromise) {
            this._assetRenditions[assetUri].ajs.renditionPromise = {};
            this._assetRenditions[assetUri].ajs.renditionZoomLevel = {};
            this._assetRenditions[assetUri].ajs.renditionShowAnnots = {};
        }
        if (options.zoomLevel) {
            this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.renditionParams.pageNum] = options.zoomLevel;
        }
        this._assetRenditions[assetUri].ajs.renditionPromise[options.renditionParams.pageNum] = ajsRenditionPromise;
        this._assetRenditions[assetUri].ajs.renditionShowAnnots[options.renditionParams.pageNum] = options.renditionParams.showAnnots;
        recordNow(constants.perfMarkers.CACHED_RENDITION);
    }

    _getMetaDataAndRendition(assetUri, options) {
        let params = { ...options, openPdfResponseCallback: data => this._openPdfResponseCallback(assetUri, data, options) };
        let ajsMetaDatPromiseResolve;
        let ajsMetaDatPromiseReject;
        const ajsMetaDataPromise = new Promise((resolve, reject) => {
            ajsMetaDatPromiseResolve = resolve;
            ajsMetaDatPromiseReject = reject;
        });
        this._cacheRendition(assetUri, {
            ajs: {
                metadataPromise: ajsMetaDataPromise,
                metadataPromiseResolve: ajsMetaDatPromiseResolve,
                metadataPromiseReject: ajsMetaDatPromiseReject,
            },
        });

        params = Object.assign(params, this._getRenditionParams(options.renditionParams));

        const {
            priority, viewState,
        } = params;

        const ajsRenditionPromise = this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "openPDFAndGetPageRendition", params, viewState, priority)
            .then(resp => {
                if (!this._assetRenditions[assetUri].ajs.renditionZoomLevel) {
                    this._assetRenditions[assetUri].ajs.renditionZoomLevel = {};
                }
                this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.renditionParams.pageNum] = resp.data.zoomLevel;
                return this._handleRenditionResponse(resp);
            }));

        if (params.shouldCacheAJSRendition && this._assetRenditions[assetUri]?.ajs) {
            this._cacheRenditionPromise(assetUri, ajsRenditionPromise, options);
        }
        return ajsMetaDataPromise;
    }

    /**
     * Gets metadata of a given PDF via AJS. Also returns downloaded PDF
     * as buffer.
     * Returns promise which resolves with metadata. Method is called with
     * the content of the file as arrayBuffer.
     *
     * @private
     */
    _ajsGetContentData(assetUri, buffer, options) {
        logEvent("ajsGetContentData Called", {
            size: buffer ? buffer.byteLength : -1,
        });

        const params = Object.assign({
            buffer,
            documentId: this._documentId,
            securityHandler: options.fetchPasswordCallback
                ? new SecurityHandler({ fetchPasswordCallback: options.fetchPasswordCallback })
                : undefined,
        }, options);

        if (options.enableMetaDataAndRenditionAPI) {
            return this._getMetaDataAndRendition(assetUri, params);
        }
        return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "openPDF", params).then(openPDFData => Object.assign({}, openPDFData.data, {
            buffer,
            acrobatJSVersion: AJSVersion,
            openPDFWasmTime: Math.round(openPDFData.timeTaken),
            openPDFQueueWaitTime: Math.round(openPDFData.queueWaitTime),
            openPDFTotalTime: Math.round(openPDFData.totalTime),
            openPDFMainToWorkerTime: Math.round(openPDFData.mainToWorkerTime),
            openPDFWorkerToMainTime: Math.round(openPDFData.workerToMainTime),
            enableWasmExceptions: openPDFData.enableWasmExceptions,
        })));
    }

    _setCompleteCNPDFAvailablePromise(assetUri) {
        if (!this._completeCNPDFAvailablePromiseMap[assetUri]) {
            this._completeCNPDFAvailablePromiseMap[assetUri] = {};
            this._completeCNPDFAvailablePromiseMap[assetUri].promise =
                new Promise(resolve => {
                    this._completeCNPDFAvailablePromiseMap[assetUri].resolve = resolve;
                });
        }
        return this._completeCNPDFAvailablePromiseMap[assetUri].promise;
    }

    /**
     * Gets metadata of a given PDF via AJS using CNPDF workflow.
     * Returns promise which resolves with metadata of the file.
     *
     * @private
     * */
    async _ajsGetCNPDFContentData(assetUri, options) {
        this._setCompleteCNPDFAvailablePromise(assetUri);
        const cnpdfFetcher = await (options.cnpdfFetcherPromise || this._external.apis.file.getCNPDFFetcher());
        const manifest = await cnpdfFetcher.getManifest();

        const params = Object.assign({
            manifest,
            cnpdfFetcher,
        }, options);

        return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "openCNPDF", params)
            .then(openPDFData => Object.assign({}, openPDFData.data, {
                enableCNPDF: true,
                acrobatJSVersion: AJSVersion,
                openPDFWasmTime: Math.round(openPDFData.timeTaken),
                openPDFQueueWaitTime: Math.round(openPDFData.queueWaitTime),
                openPDFTotalTime: Math.round(openPDFData.totalTime),
                openPDFMainToWorkerTime: Math.round(openPDFData.mainToWorkerTime),
                openPDFWorkerToMainTime: Math.round(openPDFData.workerToMainTime),
                enableWasmExceptions: openPDFData.enableWasmExceptions,
            })));
    }

    /**
     * Gets metadata of a given PDF via AJS using linearized workflow.
     * Returns promise which resolves with metadata of the file.
     *
     * @private
     * */
    _ajsGetLinearizedContentData(assetUri, options) {
        const params = {
            password: options.password,
            fetchPdfSizeCallback: () => this._fetchPDFSize(assetUri, options),
            fetchPdfBufferRangesCallback: ranges => this._fetchPDFBufferRanges(assetUri, options, ranges),
            blockSize: options.linearizationBlockSize,
            documentId: this._documentId,
        };
        return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "openLinearizedPDF", params)
            .then(openPDFData => Object.assign({}, openPDFData.data)));
    }

    _ajsDeleteRenditionCache(assetUri) {
        if (this._assetRenditions[assetUri]?.ajs?.renditionZoomLevel && this._assetRenditions[assetUri]?.ajs?.renditionShowAnnots && this._assetRenditions[assetUri]?.ajs?.renditionPromise) {
        // Delete cached object in case we failed to pick from cache.
            delete this._assetRenditions[assetUri].ajs.renditionZoomLevel;
            delete this._assetRenditions[assetUri].ajs.renditionPromise;
            delete this._assetRenditions[assetUri].ajs.renditionShowAnnots;
        }
    }

    /**
 * Gets rendition of page for a given assetURI via AJS.
 * Method should be called only when metadata of file is available.
 * Returns Promise which resolves with rendition content
 * when it is available
 *
 * @private
 */
    _ajsGetRenditionForPage(assetUri, options) {
        // TODO: Remove showAnnots hardcoded value and HC color palette check
        // Currently we pick from cache only in generic annots case and when no HC / Dark mode is present
        // We will pick from cache only if we get a second successive call for same page's rendition.
        // Else, we delete cache and don't use it again
        if (this._assetRenditions[assetUri]?.ajs.renditionPromise
            && this._assetRenditions[assetUri]?.ajs.renditionZoomLevel
            && this._assetRenditions[assetUri]?.ajs.renditionShowAnnots
            && this._assetRenditions[assetUri]?.ajs.renditionPromise[options.pageNum]
            && this._assetRenditions[assetUri]?.ajs.renditionShowAnnots[options.pageNum]
            && this._assetRenditions[assetUri]?.ajs.renditionZoomLevel[options.pageNum]
            && this._assetRenditions[assetUri]?.ajs.renditionZoomLevel[options.pageNum] === options.zoomLevel
            && this._assetRenditions[assetUri]?.ajs.renditionShowAnnots[options.pageNum] === options.showAnnots
            && !options.colorPallete && options.requestType === "page") {
            recordNow(constants.perfMarkers.CACHED_RENDITION_RETURNED);
            const val = this._assetRenditions[assetUri]?.ajs.renditionPromise[options.pageNum];
            this._ajsDeleteRenditionCache(assetUri);
            return val;
        } else if (this._assetRenditions[assetUri]?.ajs.renditionZoomLevel && this._assetRenditions[assetUri]?.ajs.renditionPromise && this._assetRenditions[assetUri]?.ajs.renditionShowAnnots) {
            const failedRenditionMarker = constants.perfMarkers.CACHED_RENDITION_FAILED;
            let reason = "";
            let error = {};
            if (!this._assetRenditions[assetUri].ajs.renditionPromise[options.pageNum] || !this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.pageNum] || !this._assetRenditions[assetUri].ajs.renditionShowAnnots[options.pageNum]) {
                reason = "PAGE_NUM";
                error = {
                    reason,
                    pageNum: options.pageNum,
                };
            } else if (this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.pageNum] !== options.zoomLevel) {
                reason = "ZOOM";
                error = {
                    reason,
                    zoomLevel: options.zoomLevel,
                    cachedZoomLevel: this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.pageNum],
                };
            } else if (options.colorPallete) {
                reason = "COLOR_PALLETE";
                error.reason = reason;
            } else if (this._assetRenditions[assetUri].ajs.renditionShowAnnots[options.pageNum] !== options.showAnnots) {
                reason = "SHOW_ANNOT";
                error = {
                    reason,
                    showAnnots: options.showAnnots,
                    cachedShowAnnots: this._assetRenditions[assetUri].ajs.renditionShowAnnots[options.pageNum],
                };
            } else if (options.requestType !== "page") {
                reason = "REQUEST_TYPE";
                error = {
                    reason,
                    requestType: options.requestType,
                };
            }
            recordNow(`${failedRenditionMarker}_${reason}`);
            logEvent("cache rendition return failed", {
                error: JSON.stringify(error),
            });
            this._ajsDeleteRenditionCache(assetUri);
        }
        // copy `options` with some default values
        const params = this._getRenditionParams(options);

        const {
            priority, viewState,
        } = params;

        const ajsRenditionPromise = this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "getPageRendition", params, viewState, priority)
            .then(resp => this._handleRenditionResponse(resp)));

        if (options.shouldCacheAJSRendition && this._assetRenditions[assetUri]?.ajs) {
            if (!this._assetRenditions[assetUri].ajs.renditionPromise) {
                this._assetRenditions[assetUri].ajs.renditionPromise = {};
                this._assetRenditions[assetUri].ajs.renditionZoomLevel = {};
                this._assetRenditions[assetUri].ajs.renditionShowAnnots = {};
            }
            if (options.zoomLevel) {
                this._assetRenditions[assetUri].ajs.renditionZoomLevel[options.pageNum] = options.zoomLevel;
            }
            this._assetRenditions[assetUri].ajs.renditionShowAnnots[options.pageNum] = options.showAnnots;
            this._assetRenditions[assetUri].ajs.renditionPromise[options.pageNum] = ajsRenditionPromise;
            recordNow(constants.perfMarkers.CACHED_RENDITION);
        }
        return ajsRenditionPromise;
    }

    /**
 * Returns the user's asset content buffers for a given assetUri.
 */
    _downloadUserAssetContentBuffer(assetUri, options) {
        const opts = options || {};
        return this._dcapiGetUserDownloadUri(assetUri, opts).then(result => (
            this._dcapiGetUserContentBuffer(assetUri, result).then(buffer => buffer)
        ));
    }

    /**
 * Performs a specified command through AJS web worker
 *
 * @private
 */
    _ajsPerformCommand(assetUri, options) {
        const { commandName } = options;

        if (commandName === "forceSwitchJS") {
            this._resetReviewProvider();
            return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "loadFullWorker"));
        }

        if (commandName === "switchToCNPDF") {
            this._setCompleteCNPDFAvailablePromise(assetUri);
        }

        if (commandName === "resetWorker") {
            // remove from cache
            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].ajs) {
                delete this._assetRenditions[assetUri].ajs;
            }
            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].dcapi) {
                delete this._assetRenditions[assetUri].dcapi.contentData;
                delete this._assetRenditions[assetUri].dcapi.contentBuffer;
                delete this._assetRenditions[assetUri].dcapi.contentFormat;
            }

            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].external) {
                delete this._assetRenditions[assetUri].external.contentBuffer;
                delete this._assetRenditions[assetUri].external.metadata;
            }

            this.closeRendition(assetUri, { ...options, cancelPromises: true });
            // Ensure that review provider gets re-initialized on re-start
            this._resetReviewProvider();
            return this._getAjs().then(() => this.ajsHandler.close(assetUri));
        }

        if (commandName === "translateToLiquidModeView") {
            return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "translateToLiquidModeView", { fetchCNFTPDFUpdatesCallback }));
        }

        if (commandName === "verifyDigitalSignatures") {
            const { externalCertificateValidator } = options;
            const certificateChainValidators = getCertificateValidators(externalCertificateValidator);
            return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, "verifyDigitalSignatures", { certificateChainValidators }));
        }

        if (commandName === "remapAJSCache") {
            const { oldAssetUri, newAssetUri } = options;
            const toBeDeletedAssetUriIndex = this._assetRenditionsIndex.indexOf(oldAssetUri);

            if (this._assetRenditions[oldAssetUri] && this._assetRenditions[oldAssetUri].ajs) {
                this._assetRenditions[newAssetUri] = this._assetRenditions[newAssetUri] || {};
                this._assetRenditions[newAssetUri].ajs = this._assetRenditions[oldAssetUri].ajs;
                this._assetRenditionsIndex.splice(toBeDeletedAssetUriIndex, 1);
                if (!this._assetRenditionsIndex.includes(newAssetUri)) {
                    this._assetRenditionsIndex.push(newAssetUri);
                }
            }
            if (this._completePDFBufferAvailablePromiseMap[oldAssetUri]) {
                this._completePDFBufferAvailablePromiseMap[newAssetUri] = this._completePDFBufferAvailablePromiseMap[oldAssetUri];
                delete this._completePDFBufferAvailablePromiseMap[oldAssetUri];
            }
            if (this._waitOnCompletePDFBufferMap[oldAssetUri]) {
                this._waitOnCompletePDFBufferMap[newAssetUri] = this._waitOnCompletePDFBufferMap[oldAssetUri];
                delete this._waitOnCompletePDFBufferMap[oldAssetUri];
            }
            this._getAjs().then(() => this.ajsHandler.remapAssetUri(oldAssetUri, newAssetUri));
            return Promise.resolve();
        }

        const {
            priority,
            viewState,
            ...params
        } = options;

        return this._getAjs().then(() => this.ajsHandler.callAsync(assetUri, commandName, params, viewState, priority));
    }

    /**
 * Performs a specified command through DCAPI
 *
 * @private
 */
    _dcapiPerformCommand(assetUri, options) {
        return this.dcapiPromise.then(() => new Promise((resolve, reject) => {
            if (options.commandName === "setLastPageNum") {
                this._dcapiSetLastPageNum(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else if (options.commandName === "setLastAccess") {
                this._dcapiSetLastAccess(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else if (options.commandName === "getContentBuffer") {
                this.downloadAssetContentBuffer(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else if (options.commandName === "getUserContentBuffer") {
                this._downloadUserAssetContentBuffer(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else {
                reject();
            }
        }));
    }

    /**
 * Performs a specified command through External
 *
 * @private
 */
    _externalPerformCommand(assetUri, options) {
        return new Promise((resolve, reject) => {
            if (options.commandName === "setLastPageNum") {
                resolve();
            } else if (options.commandName === "getContentBuffer") {
                this.downloadAssetContentBuffer(assetUri, options)
                    .then(resolve)
                    .catch(reject);
            } else if (options.commandName === "setLastAccess") {
                resolve();
            } else if (options.commandName === "getUserContentBuffer") {
                resolve();
            } else {
                reject();
            }
        });
    }

    /**
 * Returns a promise which resolves when both dcapi and ajs backends
 * are ready (if enabled)
 *
 */
    ready() {
        return Promise.resolve(this);
    }

    canPerformSync(assetUri) {
        return {
            canPerformSync: this._getAjs().then(() => this.ajsHandler.isInstanceCached(assetUri)),
            ready: this._getAjs().then(() => this.ajsHandler.activateInstanceIfInactive(assetUri)),
        };
    }

    /**
     *
     * This method will be used for JS related apis only, and main thread is blocked using while loop
     * just after making this call. Do not introduce any asynchronicity in this method as it will never be
     * resolved because main thread is blocked
     */
    performCommandSynchronously(assetUri, options) {
        const {
            priority,
            viewState,
            ...params
        } = options;
        return this.ajsHandler.callSync(assetUri, options.commandName, params, viewState, priority);
    }

    /**
 * Common interface to invoke AJS/DCAPI specific rendition APIs.
 * AJS commands work only for PDF files.
 * Returns an object with a promise which resolves when the
 * specified ajs/dcapi command is done.
 */
    performCommand(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;
        options.enableExternal = options.enableExternal !== false && this.options.enableExternal;

        if (options.useAJS) {
            returnVal.ajs = this._ajsPerformCommand(assetUri, options);
        } else {
            returnVal.ajs = Promise.reject(new Error("Cannot perform AJS command if AJS is disabled"));
        }

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiPerformCommand(assetUri, options);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot perform DCAPI command if DCAPI is disabled"));
        }

        if (options.enableExternal && RenditionAPI._isExternalAsset(options)) {
            returnVal.external = this._externalPerformCommand(assetUri, options);
        }

        return returnVal;
    }

    /**
     * Returns a promise which resolves once a whole pdf is available.
     */
    isCompletePDFBufferAvailable(assetUri) {
        return this._completePDFBufferAvailablePromiseMap[assetUri].promise;
    }


    /**
     * Returns a promise which resolves once all components of a cnpdf are available.
     */
    isCompleteCNPDFAvailable(assetUri) {
        return this._setCompleteCNPDFAvailablePromise(assetUri);
    }

    /**
 * Gets metadata of a given assetUri.
 * Returns an object with a promise for ajs, dcapi and external respectively.
 * The promises resolve when metadata is available from the respective
 * backends.
 */
    getMetadata(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};
        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;
        options.enableExternal = options.enableExternal !== false && this.options.enableExternal;
        this.options.shouldUseModernViewer = options.shouldUseModernViewer;

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiGetMetadata(assetUri, options);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot get DCAPI metadata if DCAPI is disabled"));
        }

        if (options.enableExternal && RenditionAPI._isExternalAsset(options)) {
            returnVal.external = this._externalGetMetadata(assetUri, options);
        }

        if (options.useAJS) {
            if (!this._completePDFBufferAvailablePromiseMap[assetUri]) {
                this._completePDFBufferAvailablePromiseMap[assetUri] = {};
                this._completePDFBufferAvailablePromiseMap[assetUri].promise = new Promise(resolve => {
                    this._completePDFBufferAvailablePromiseMap[assetUri].resolve = resolve;
                });
            }
            returnVal.ajs = this._ajsGetMetadata(assetUri, options);
        } else {
            returnVal.ajs = Promise.reject(new Error("Cannot get AJS metadata if AJS is disabled"));
        }

        if (!this._waitOnCompletePDFBufferMap[assetUri]) {
            this._waitOnCompletePDFBufferMap[assetUri] = {};
            this._waitOnCompletePDFBufferMap[assetUri].promise = new Promise(resolve => {
                this._waitOnCompletePDFBufferMap[assetUri].resolve = resolve;
            });
        }
        return returnVal;
    }

    _ajsRegisterLicense(opts = {}) {
        const params = {
            token: opts.licenseString,
            domain: opts.domain,
        };
        return this._getAjs().then(() => this.ajsHandler.registerLicense(params));
    }

    registerLicense(opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;

        returnVal.dcapi = Promise.reject(new Error("DCAPI doesn't support sdk license validation"));

        if (options.useAJS) {
            returnVal.ajs = this._ajsRegisterLicense(options);
        } else {
            returnVal.ajs = Promise.reject(new Error("Cannot register license if AJS is disabled"));
        }

        return returnVal;
    }

    /**
 * Gets rendition of a given assetUri. 'options' argument can be used to
 * specify 'size', 'format', 'page' etc of the rendition
 * Returns an object with a promise for ajs and dcapi respectively.
 * The promises resolve when rendition is available from the respective
 * backends.
 */
    getRendition(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;
        options.enableExternal = options.enableExternal !== false && this.options.enableExternal;

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiGetRendition(assetUri, options);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot get DCAPI rendition if DCAPI is disabled"));
        }

        if (options.enableExternal && RenditionAPI._isExternalAsset(options)) {
            returnVal.external = this._externalGetRendition(assetUri, options);
        }

        if (options.useAJS) {
            returnVal.ajs = this._ajsGetRenditionForPage(assetUri, options);
        } else {
            returnVal.ajs = Promise.reject(new Error("Cannot get AJS rendition if AJS is disabled"));
        }

        return returnVal;
    }


    /**
 * Gets document rendition URI of a given assetUri. 'options' argument can be used to
 * specify 'format' of the rendition
 * Returns an object with a promise for ajs and dcapi respectively.
 * The promises resolve with the uri to the document rendition
 */
    getDocumentRenditionUri(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiGetDocumentRenditionUri(assetUri, options);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot get DCAPI doc rendition URI if DCAPI is disabled"));
        }

        returnVal.ajs = Promise.reject(new Error("Cannot get AJS doc rendition URI"));

        return returnVal;
    }

    /**
 * Gets document rendition of a given assetUri. 'options' argument can be used to
 * specify 'format' of the rendition
 * Returns an object with a promise for ajs and dcapi respectively.
 * The promises resolve with the document rendition content
 */
    getDocumentRendition(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiGetDocumentRendition(assetUri, options);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot get DCAPI doc rendition if DCAPI is disabled"));
        }

        returnVal.ajs = Promise.reject(new Error("Cannot get AJS doc rendition"));

        return returnVal;
    }

    /**
 * Gets document rendition status of a given assetUri
 * Returns an object with a promise for ajs and dcapi respectively.
 * The promises resolve with the object containing status flags for each
 * format of document rendition.
 */
    getDocumentRenditionStatus(assetUri, opts) {
        const returnVal = {
        };

        const options = opts || {};

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;

        if (options.useDCAPI) {
            returnVal.dcapi = this._dcapiGetDocumentRenditionStatus(assetUri);
        } else {
            returnVal.dcapi = Promise.reject(new Error("Cannot get DCAPI doc rendition status if DCAPI is disabled"));
        }

        returnVal.ajs = Promise.reject(new Error("Cannot get DCAPI doc rendition status"));

        return returnVal;
    }

    /**
 * Returns the asset's content buffers for a given assetUri.
 */
    downloadAssetContentBuffer(assetUri, opts) {
        // yguptaTODO: Make this method private after preview dropin switches to using performCommand
        // to trigger this method
        if (opts && RenditionAPI._isExternalAsset(opts)) {
            return this._externalGetDownloadUri(assetUri, opts).then(result => (
                this._externalGetContentBuffer(assetUri, result).then(buffer => buffer)
            ));
        }
        if (!this._assetRenditions[assetUri] ||
            !this._assetRenditions[assetUri].dcapi ||
            !this._assetRenditions[assetUri].dcapi.contentBuffer) {
            return this._dcapiGetDownloadUri(assetUri, opts).then(result => (
                this._dcapiGetContentBuffer(assetUri, result, opts).then(buffer => buffer)
            ));
        }
        return Promise.resolve(this._assetRenditions[assetUri].dcapi.contentBuffer);
    }

    updateDefaultAJSInstance(assetUri) {
        this.ajsHandler.updateDefaultAJSInstance(assetUri);
    }

    /**
     * deletes cached rendition data.
     */
    clearRenditionCache(assetUri, opts) {
        const options = opts || {};
        const assetKey = `${assetUri}`;

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;
        options.useAJS = options.useAJS !== false && this.options.useAJS;
        options.enableExternal = options.enableExternal !== false && this.options.enableExternal;

        if (options.useAJS) {
            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].ajs) {
                delete this._assetRenditions[assetUri].ajs;
            }
        }
        if (options.useDCAPI) {
            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].dcapi) {
                delete this._assetRenditions[assetUri].dcapi;
            }
        }
        if (options.enableExternal) {
            if (this._assetRenditions[assetUri] && this._assetRenditions[assetUri].external) {
                delete this._assetRenditions[assetUri].external;
            }
        }
        if (this._getAssetFileInfoPromise[assetKey]) {
            delete this._getAssetFileInfoPromise[assetKey];
        }
        if (window.adobe_dc_sdk.cache && window.adobe_dc_sdk.cache.assetURLPromise) {
            delete window.adobe_dc_sdk.cache.assetURLPromise;
        }

        return Promise.resolve();
    }

    /**
 * Performs the work required while closing the rendition.
 * Currently would clear the cache and set the last page num metadata
 * if asked to do so.
 *
 */
    closeRendition(assetUri, opts) {
        const returnVal = {
            dcapi: Promise.resolve(),
            ajs: Promise.resolve(),
        };

        const options = opts || {};
        const assetKey = `${assetUri}`;

        options.useDCAPI = options.useDCAPI !== false && this.options.useDCAPI;

        if (this._assetRenditions[assetUri] && options.clearCache) {
            delete this._assetRenditions[assetUri];
            this._assetRenditionsIndex = this._assetRenditionsIndex.filter(uri => uri !== assetUri);
        }

        if (options.clearCache) {
            this._assetPageRenditions = Object.entries(this._assetPageRenditions).reduce((acc, [key, val]) => {
                if (!key.startsWith(assetUri)) {
                    acc[key] = val;
                }
                return acc;
            }, {});
            this._assetPageRenditionsIndex = Object.keys(this._assetPageRenditions);
            const assetPageRenditionPromisesEntries =
                        Object.entries(this._assetPageRenditionPromises);
            this._assetPageRenditionPromises = assetPageRenditionPromisesEntries.reduce((acc, [key, val]) => {
                if (!key.startsWith(assetUri)) {
                    acc[key] = val;
                }
                return acc;
            }, {});
        }
        if (this._getAssetFileInfoPromise[assetKey]) {
            delete this._getAssetFileInfoPromise[assetKey];
        }
        if (window.adobe_dc_sdk.cache && window.adobe_dc_sdk.cache.assetURLPromise) {
            delete window.adobe_dc_sdk.cache.assetURLPromise;
        }

        if (options.useDCAPI && options.lastPageNum !== undefined) {
            returnVal.dcapi = this._dcapiSetLastPageNum(assetUri, options);
        }

        if (options.cancelPromises) {
            cancelAllPromises();
        }
    }


    /* eslint-disable class-methods-use-this */
    /**
 * Public method to get script-src of rendition.js
 * This is needed because Sign manage page overrides it
 *
 */
    getOwnScriptSrc() {
        return ajsPathUtil.getScriptSrc(constants.RENDITION_SCRIPT_NAME);
    }

    /**
 * get AJS worker - allow override by caller
 *
 * @return {Worker}
 */
    getAJSWorker() {
        return undefined;
    }

    /**
 * returns a promise which resolves only if, due to some reason, we need to wait on the
 * complete pdf buffer when linearization is enabled. One such reason is, if linearized
 * open pdf fails and we fallback to non-linearized open pdf.
 */
    shouldWaitOnCompletePDFBuffer(assetUri) {
        return this._waitOnCompletePDFBufferMap[assetUri].promise;
    }
}

function renditionLoad() {
    return Promise.resolve(RenditionAPI);
}

function loadFontProvider(serverPath) {
    __webpack_public_path__ = serverPath; /* eslint-disable-line */
    return import(/* webpackChunkName: "fontHelper" */ "./fontProvider/FontManager")
        .then(provider => provider.default);
}

function loadAJSProvider(serverPath) {
    __webpack_public_path__ = serverPath; /* eslint-disable-line */
    return import(/* webpackChunkName: "ajsProvider" */ "./ajsProvider/AcrobatJSProvider")
        .then(provider => provider.default);
}

const providers = {
    rendition: {
        load: renditionLoad,
    },
    "font-provider": {
        load: loadFontProvider,
    },
    acrobatjs: {
        load: loadAJSProvider,
    },
};

export { providers };

RenditionAPI.getInstance = getSingletonFunction(RenditionAPI, /* argsRequired*/true);
serviceAPI.rendition = RenditionAPI.getInstance;
